import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { parseUtc, formatDateTime, formatTrackingEventDate } from '../utils/dates';
import axios from 'axios';
import './MessageWindow.css';
import { Link, useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import InfoPanel from './InfoPanel';
import PackageIdentifierCards from './PackageIdentifierCards';
import { useTranslation } from 'react-i18next';
import { 
    FaPaperPlane, 
    FaLightbulb, 
    FaCheck, 
    FaTimes, 
    FaLanguage, 
    FaTruck, 
    FaUser, 
    FaRobot, 
    FaEllipsisV, 
    FaEdit,
    FaFile,
    FaTimesCircle,
    FaInfoCircle,
    FaBox,
    FaShippingFast,
    FaProjectDiagram,
    FaCheckCircle,
    FaBarcode,
    FaFilePdf,
    FaFileImage,
    FaFileAlt,
    FaFileArchive,
    FaFileCode,
    FaTag,
    FaBuilding,
    FaMapMarkerAlt,
    FaCity,
    FaGlobe,
    FaPhone,
    FaEnvelope,
    FaIdCard,
    FaInfo,
    FaMoneyBillWave,
    FaBoxOpen,
    FaShieldAlt,
    FaReceipt,
    FaComments,
    FaHandPaper,
    FaExclamationTriangle,
    FaUserPlus,
    FaCircleNotch,
    FaStickyNote,
    FaPlus,
    FaThumbsUp,
    FaThumbsDown,
    FaWarehouse,
    FaArrowLeft,
    FaArrowRight,
    FaHistory,
    FaClock,
    FaTicketAlt,
    FaSlack,
    FaChevronDown,
    FaChevronUp,
    FaFilter,
    FaInstagram,
    FaFacebookMessenger,
    FaWhatsapp,
    FaStore,
    FaSync,
    FaPaperclip,
    FaClipboardList,
    FaShareSquare,
    FaBug,
    FaCopy,
    FaUsers,
    FaExternalLinkAlt
} from 'react-icons/fa';
import CarriersConversation from './CarriersConversation';
import ManualConversation from './ManualConversation';
import TicketActors from './TicketActors';
import { outsideAuthor, outsideSourceLabel } from '../utils/ticketActors';
import WarehouseConversation from './WarehouseConversation';
import AiRunPanel from './AiRunPanel';
import AiProposalsPanel from './AiProposalsPanel';
import LinkifiedText from './LinkifiedText';
import { fetchStoredSummary, generateSummary, summaryNeedsUpdate } from '../utils/ticketSummary';
import AiTimelineOverlay from './AiTimelineOverlay';
import ColissimoLabels from './ColissimoLabels';
import TicketRefunds from './TicketRefunds';

import { EDN_STATUS_MAPPING, TERMINAL_READ_STATUS_VALUES, getStatusLabel } from '../constants/ticketStatuses';
import TicketAgentNotices from './TicketAgentNotices';

const getStoredUserData = () => {
    try {
        const storedUserData = localStorage.getItem('userData') || localStorage.getItem('user_data');
        return storedUserData ? JSON.parse(storedUserData) : null;
    } catch (error) {
        return null;
    }
};

const normalizeEdnTopicOption = (topic) => {
    const topicId = topic?.id ?? topic?.ticket_topic_id ?? topic?.ticket_topic;
    return {
        id: Number(topicId),
        value: String(topicId),
        label: topic?.topic_full || topic?.name || topic?.topic || String(topicId),
        category: topic?.topic || topic?.category || '',
        type: 'edn'
    };
};

const MessageWindow = () => {
    const { t } = useTranslation();
    const { ticketId } = useParams();
    // Latest ticket id for the WebSocket handler (its closure is created once).
    const ticketIdRef = useRef(ticketId);
    useEffect(() => {
        ticketIdRef.current = ticketId;
    }, [ticketId]);
    // Deep-linkable side-panel tab (?tab=carriers|manual|track), e.g. from the copilot.
    const [searchParams] = useSearchParams();
    const requestedTab = searchParams.get('tab');

    // Channel icons mapping
    const channelIcons = {
        Instagram: FaInstagram,
        Messenger: FaFacebookMessenger,
        WhatsApp: FaWhatsapp,
        Gmail: FaEnvelope,
        Outlook: FaEnvelope,
        Website: FaGlobe,
        Custom: FaEnvelope,
        Ticketing: FaTicketAlt,
        'ERP API': FaTicketAlt
    };
    const [ticket, setTicket] = useState(null);
    // The conversation this ticket continues / the tickets that continue it
    // (a new request after several days on WhatsApp / Messenger / Instagram).
    const [relatedTickets, setRelatedTickets] = useState({ previous: null, next: [] });
    const [messages, setMessages] = useState([]);
    const [employee, setEmployee] = useState('');
    const [aiAgentName, setAiAgentName] = useState('');
    const [trackingCopied, setTrackingCopied] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const [editingMessage, setEditingMessage] = useState(null);
    const [editedContent, setEditedContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [userLoading, setUserLoading] = useState(true);
    const [targetLanguage, setTargetLanguage] = useState('original');
    const [translatedMessages, setTranslatedMessages] = useState({});
    const [customerLoading, setCustomerLoading] = useState(false);
    const [orderLoading, setOrderLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [overlayState, setOverlayState] = useState({ show: false, message: '', success: false });
    const [user, setUser] = useState(null);
    const [clientInfo, setClientInfo] = useState('');
    const [orderDetails, setOrderDetails] = useState('');
    const [summary, setSummary] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);
    const [carriersConversation, setCarriersConversation] = useState(null);
    const [continuationMessage, setContinuationMessage] = useState('');
    const [testUserId, setTestUserId] = useState('');
    const [testThreadId, setTestThreadId] = useState('');
    const [testAssistantId, setTestAssistantId] = useState('');
    const [validatingMessage, setValidatingMessage] = useState(false);
    const [activeTab, setActiveTab] = useState(
        ['carriers', 'manual', 'track', 'runs', 'actors'].includes(requestedTab) ? requestedTab : 'carriers'
    );
    // AI trace focus: set by the "Run IA" links on AI messages and actions (docs/ai-trace-spec.md §6.2)
    const [aiRunFocus, setAiRunFocus] = useState(null);
    const [aiTimelineOpen, setAiTimelineOpen] = useState(false);
    // Opens the full-screen chronology focused on a run / span (from a message,
    // an AI action, or the « Runs IA » tab).
    const openAiRun = (runId, spanId) => {
        setAiRunFocus({ runId: runId || null, spanId: spanId || null, at: Date.now() });
        setAiTimelineOpen(true);
    };
    const closeAiTimeline = useCallback(() => setAiTimelineOpen(false), []);
    const [showActionMenu, setShowActionMenu] = useState(false);
    const actionMenuRef = useRef(null);
    const attachmentsDropdownRef = useRef(null);
    const [showAttachmentsDropdown, setShowAttachmentsDropdown] = useState(false);
    // KI-225: decorative images (signatures, logos) are kept, folded away.
    const [showDecorativeFiles, setShowDecorativeFiles] = useState(false);
    const [expandedDecorative, setExpandedDecorative] = useState({});
    // KI-229: the quoted thread of an e-mail, folded under the sender's words.
    const [expandedQuotedHistory, setExpandedQuotedHistory] = useState({});

    // Add missing state variables for employee assignment
    const [employees, setEmployees] = useState([]);
    const [showAssignmentDropdown, setShowAssignmentDropdown] = useState(false);
    const [assigningTicket, setAssigningTicket] = useState(false);
    const assignmentDropdownRef = useRef(null);

    // State for ticket files
    const [ticketFiles, setTicketFiles] = useState([]);
    const [messageFiles, setMessageFiles] = useState({});
    const [filesLoading, setFilesLoading] = useState(true);
    // KI-225: the stored objects the platform judged decorative (signature,
    // logo, icon) — a message's copy of the file is matched on its object.
    const decorativeUrls = useMemo(() => new Set(
        ticketFiles.filter((file) => file?.metadata?.file_class === 'decorative').map((file) => file?.s3_url).filter(Boolean)
    ), [ticketFiles]);
    const isDecorativeFile = useCallback((file) => (
        file?.metadata?.file_class === 'decorative' || Boolean(file?.s3_url && decorativeUrls.has(file.s3_url))
    ), [decorativeUrls]);
    const documentTicketFiles = useMemo(() => ticketFiles.filter((file) => !isDecorativeFile(file)), [ticketFiles, isDecorativeFile]);
    const decorativeTicketFiles = useMemo(() => ticketFiles.filter((file) => isDecorativeFile(file)), [ticketFiles, isDecorativeFile]);
    // State for file overlay
    const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState(null);

    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const pingIntervalRef = useRef(null);
    const fetchMessagesRef = useRef(null);
    const latestClientEmailRef = useRef('');
    const textareaRef = useRef(null);
    const lastFetchedTicketIdRef = useRef(null); // Prevent duplicate fetches
    const presenceIntervalRef = useRef(null); // For presence heartbeat
    const activePresenceTicketRef = useRef(null);
    const presenceCloseTimeoutsRef = useRef({});

    // Add new state for context data
    const [contextData, setContextData] = useState(null);
    const [contextLoading, setContextLoading] = useState(true);
    const [clientTickets, setClientTickets] = useState([]);
    const [clientTicketsLoading, setClientTicketsLoading] = useState(false);
    const [clientTicketsError, setClientTicketsError] = useState(null);
    const [showAllClientTickets, setShowAllClientTickets] = useState(false);

    // Add new state for warehouse email form
    const [warehouseEmailForm, setWarehouseEmailForm] = useState({
        subject: '',
        content: '',
        recipient_email: ''
    });

    // Add new state for niveau2 email form
    const [niveau2EmailForm, setNiveau2EmailForm] = useState({
        subject: '',
        content: '',
        recipient_email: '',
        selectedFiles: []
    });

    // Add new state for file selection
    const [selectedTicketFiles, setSelectedTicketFiles] = useState([]);

    // Add new state for warehouse conversation
    const [warehouseConversation, setWarehouseConversation] = useState(null);
    const [warehouseLoading, setWarehouseLoading] = useState(true);

    // Add new state for file upload functionality
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const fileInputRef = useRef(null);

    const attachmentList = useMemo(() => {
        const ticketAttachments = ticketFiles.map((file, index) => ({
            file,
            source: 'ticket',
            parentId: ticketId,
            order: index,
        }));

        const messageAttachments = messages.reduce((acc, message) => {
            const files = messageFiles[message._id] || [];
            files.forEach((file, index) => {
                acc.push({
                    file,
                    source: 'message',
                    parentId: message._id,
                    order: index,
                    createdAt: message.created_at,
                });
            });
            return acc;
        }, []);

        return [...ticketAttachments, ...messageAttachments];
    }, [ticketFiles, messageFiles, messages, ticketId]);

    const selectedAttachment =
        selectedAttachmentIndex !== null ? attachmentList[selectedAttachmentIndex] : null;
    const selectedFile = selectedAttachment?.file || null;
    const totalAttachments = attachmentList.length;

    // Add new state for notes functionality
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [noteContent, setNoteContent] = useState('');
    const [selectedMessageForNote, setSelectedMessageForNote] = useState(null);
    const [addingNote, setAddingNote] = useState(false);

    // Add new state for AI help loading
    const [gettingAiHelp, setGettingAiHelp] = useState(false);

    // Add new state for tracking which draft message is being sent
    const [sendingDraftMessageId, setSendingDraftMessageId] = useState(null);
    // Drafts whose held carrier contacts the operator chose NOT to send with
    // the reply (KI-108); by default they travel with it.
    const [skipDraftCarrierContacts, setSkipDraftCarrierContacts] = useState({});
    const [ednVisibilityModal, setEdnVisibilityModal] = useState({
        show: false,
        action: null,
        messageId: null
    });

    // State for draft message file attachments
    const [draftPendingFiles, setDraftPendingFiles] = useState({});
    const [activeDraftForFileUpload, setActiveDraftForFileUpload] = useState(null);
    const draftFileInputRef = useRef(null);

    // Add new state for AI handoff functionality
    const [showHandoffModal, setShowHandoffModal] = useState(false);
    const [handoffInstructions, setHandoffInstructions] = useState('');
    const [handingOffToAI, setHandingOffToAI] = useState(false);
    const [expandedHandoffMessages, setExpandedHandoffMessages] = useState({});

    // Add new state for AI action expansion
    const [expandedAiActions, setExpandedAiActions] = useState({});

    // Add new state for note expansion per message
    const [expandedNotes, setExpandedNotes] = useState({});

    // Add new state for Slack message functionality
    const [showSlackModal, setShowSlackModal] = useState(false);
    const [slackMessage, setSlackMessage] = useState('');
    const [sendingSlackMessage, setSendingSlackMessage] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);

    // State for bug report functionality
    const [showBugReportModal, setShowBugReportModal] = useState(false);
    const [bugDescription, setBugDescription] = useState('');
    const [sendingBugReport, setSendingBugReport] = useState(false);

    // State for email forwarding functionality
    const [showForwardModal, setShowForwardModal] = useState(false);
    const [forwardToInput, setForwardToInput] = useState('');
    const [forwardCcInput, setForwardCcInput] = useState('');
    const [forwardComment, setForwardComment] = useState('');
    const [forwardingEmail, setForwardingEmail] = useState(false);

    const [hideDraftMessages, setHideDraftMessages] = useState(false);

    // State for brand info
    const [brand, setBrand] = useState(null);

    // State for pending files to be sent with message
    const [pendingFiles, setPendingFiles] = useState([]);

    // State for topic editing
    const [availableTopics, setAvailableTopics] = useState([]);
    const [showTopicDropdown, setShowTopicDropdown] = useState(false);
    const [updatingTopic, setUpdatingTopic] = useState(false);
    const [topicsLoading, setTopicsLoading] = useState(false);
    const topicDropdownRef = useRef(null);

    // State for the ticket client picker (a client never identified at intake
    // blocks the client-mapped carrier portal accounts — KI-65)
    const [clientOptions, setClientOptions] = useState([]);
    const [clientOptionsLoaded, setClientOptionsLoaded] = useState(false);
    const [clientOptionsLoading, setClientOptionsLoading] = useState(false);
    const [showClientDropdown, setShowClientDropdown] = useState(false);
    const [updatingClient, setUpdatingClient] = useState(false);
    const [clientFilter, setClientFilter] = useState('');
    const clientDropdownRef = useRef(null);

    // State for EDN status editing
    const [availableEdnStatuses, setAvailableEdnStatuses] = useState([]);
    const [showEdnStatusDropdown, setShowEdnStatusDropdown] = useState(false);
    const [ednStatusesLoading, setEdnStatusesLoading] = useState(false);
    const [updatingEdnStatus, setUpdatingEdnStatus] = useState(false);
    const ednStatusDropdownRef = useRef(null);

    // State for ticket summary
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [ticketSummary, setTicketSummary] = useState('');
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [showStatusHistoryModal, setShowStatusHistoryModal] = useState(false);
    const [statusHistoryData, setStatusHistoryData] = useState(null);
    const [loadingStatusHistory, setLoadingStatusHistory] = useState(false);

    // State for restart AI processing
    const [showRestartAIModal, setShowRestartAIModal] = useState(false);
    const [availableAssistants, setAvailableAssistants] = useState([]);
    const [assistantsLoading, setAssistantsLoading] = useState(false);
    const [selectedAssistantId, setSelectedAssistantId] = useState('');
    const [restartingAI, setRestartingAI] = useState(false);

    const fetchTicketContext = async () => {
        setContextLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/${ticketId}/context`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setContextData(response.data);
        } catch (error) {
        } finally {
            setContextLoading(false);
        }
    };

    const fetchAvailableTopics = async () => {
        setTopicsLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            if (isEdnCustomUser) {
                const response = await axios.get('/api/edn_tms/edn_tms/ticket/topics', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setAvailableTopics((response.data?.topics || []).map(normalizeEdnTopicOption));
            } else {
                const response = await axios.get('/api/tickets/available-topics', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const segmentOptions = (response.data.all_topics || []).map((topic) => ({
                    ...topic,
                    kind: 'segment',
                }));
                // Standard classification topics (independent axis: they never
                // overwrite the routing topic) when the tenant enabled them.
                let classificationOptions = [];
                try {
                    const configResponse = await axios.get('/api/logistics/topics-config', {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (configResponse.data?.classification_enabled) {
                        classificationOptions = (configResponse.data.catalog || []).map((entry) => ({
                            value: entry.slug,
                            label: entry.name,
                            kind: 'classification',
                        }));
                    }
                } catch (configError) {
                    // Employees may not have access to the config endpoint; the
                    // segment options alone are still valid.
                }
                setAvailableTopics([...segmentOptions, ...classificationOptions]);
            }
        } catch (error) {
            console.error('Failed to fetch available topics:', error);
        } finally {
            setTopicsLoading(false);
        }
    };

    const handleUpdateTopic = async (topicOption) => {
        const newTopic = typeof topicOption === 'object' ? topicOption.value : topicOption;
        if (!newTopic || updatingTopic) return;

        // Classification options set what the ticket is ABOUT
        // (topic_category/topic_name) and never touch the routing topic.
        const isClassificationOption = !isEdnCustomUser
            && typeof topicOption === 'object'
            && topicOption.kind === 'classification';

        setUpdatingTopic(true);
        try {
            const token = localStorage.getItem('access_token');
            let updatedTopicLabel = newTopic;
            let updatedTopicCategory = newTopic;
            let updatedTopicId = null;

            if (isEdnCustomUser) {
                updatedTopicId = Number(topicOption?.id ?? newTopic);
                const response = await axios.put('/api/edn_tms/edn_tms/ticket/topic',
                    { ticket_id: ticketId, ticket_topic_id: updatedTopicId },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const ticketTopic = response.data?.ticket_topic || {};
                updatedTopicLabel = ticketTopic.topic_full || topicOption?.label || newTopic;
                updatedTopicCategory = ticketTopic.topic || topicOption?.category || updatedTopicLabel;
            } else if (isClassificationOption) {
                const response = await axios.put(`/api/tickets/${ticketId}/topic-classification`,
                    { topic: newTopic },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                updatedTopicCategory = response.data?.topic_category || newTopic;
                updatedTopicLabel = response.data?.topic_name || topicOption?.label || newTopic;
            } else {
                await axios.put(`/api/tickets/${ticketId}/topic`,
                    { topic: newTopic },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }

            const applyEdnOrClassif = isEdnCustomUser || isClassificationOption;

            if (contextData) {
                setContextData(prev => ({
                    ...prev,
                    shipping_info: {
                        ...prev.shipping_info,
                        topic: applyEdnOrClassif ? updatedTopicCategory : newTopic,
                        topic_id: isEdnCustomUser ? updatedTopicId : prev.shipping_info?.topic_id,
                        topic_name: applyEdnOrClassif ? updatedTopicLabel : prev.shipping_info?.topic_name
                    }
                }));
            }

            setTicket(prev => prev ? {
                ...prev,
                // The routing topic only changes when a segment option is picked
                topic: applyEdnOrClassif ? prev.topic : newTopic,
                topic_id: isEdnCustomUser ? updatedTopicId : prev.topic_id,
                ticket_topic_id: isEdnCustomUser ? updatedTopicId : prev.ticket_topic_id,
                topic_name: applyEdnOrClassif ? updatedTopicLabel : prev.topic_name,
                topic_category: applyEdnOrClassif ? updatedTopicCategory : prev.topic_category,
                ticket_topic: applyEdnOrClassif ? {
                    ...(prev.ticket_topic || {}),
                    id: isEdnCustomUser ? updatedTopicId : prev.ticket_topic?.id,
                    ticket_topic_id: isEdnCustomUser ? updatedTopicId : prev.ticket_topic?.ticket_topic_id,
                    name: updatedTopicLabel,
                    topic: updatedTopicCategory,
                    topic_full: updatedTopicLabel,
                    category: updatedTopicCategory
                } : prev.ticket_topic
            } : prev);
            
            setOverlayState({
                show: true,
                message: t('messageWindow.topicUpdateSuccess', 'Topic updated successfully'),
                success: true
            });
            setShowTopicDropdown(false);
        } catch (error) {
            const errorMessage = error.response?.data?.detail || t('messageWindow.topicUpdateError', 'Failed to update topic');
            setOverlayState({
                show: true,
                message: errorMessage,
                success: false
            });
        } finally {
            setUpdatingTopic(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
        }
    };

    const handleTopicDropdownToggle = () => {
        if (!showTopicDropdown && availableTopics.length === 0) {
            fetchAvailableTopics();
        }
        setShowTopicDropdown(!showTopicDropdown);
    };

    const fetchClientOptions = async () => {
        setClientOptionsLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/${ticketId}/client-options`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setClientOptions(response.data?.clients || []);
            setClientOptionsLoaded(true);
        } catch (error) {
            console.error('Failed to fetch client options:', error);
        } finally {
            setClientOptionsLoading(false);
        }
    };

    const handleClientDropdownToggle = () => {
        if (!showClientDropdown && !clientOptionsLoaded) {
            fetchClientOptions();
        }
        setClientFilter('');
        setShowClientDropdown(!showClientDropdown);
    };

    // Sets the ticket's client only: no enrichment re-run, no AI restart. A
    // carrier request refused for « no identified client » goes through once
    // approved again (the portal account is chosen from this client).
    const handleSelectClient = async (clientOption) => {
        if (!clientOption?.id || updatingClient) return;
        setUpdatingClient(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.put(`/api/tickets/${ticketId}/client`,
                { client_id: clientOption.id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const client = response.data?.client || {};
            setContextData(prev => prev ? {
                ...prev,
                client_info: {
                    ...(prev.client_info || {}),
                    id: client.id,
                    name: client.name || clientOption.name,
                    source: client.source || 'operator',
                },
            } : prev);
            setOverlayState({
                show: true,
                message: t('messageWindow.clientUpdateSuccess', 'Client set on the ticket'),
                success: true
            });
            setShowClientDropdown(false);
        } catch (error) {
            const detail = error.response?.data?.detail;
            setOverlayState({
                show: true,
                message: typeof detail === 'string' ? detail : t('messageWindow.clientUpdateError', 'Failed to set the client'),
                success: false
            });
        } finally {
            setUpdatingClient(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
        }
    };

    const fetchEdnStatuses = async () => {
        setEdnStatusesLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get('/api/edn_tms/edn_tms/status', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAvailableEdnStatuses(response.data?.status_list || EDN_STATUS_MAPPING);
        } catch (error) {
            setAvailableEdnStatuses(EDN_STATUS_MAPPING);
        } finally {
            setEdnStatusesLoading(false);
        }
    };

    const handleEdnStatusDropdownToggle = () => {
        if (!showEdnStatusDropdown && availableEdnStatuses.length === 0) {
            fetchEdnStatuses();
        }
        setShowEdnStatusDropdown(!showEdnStatusDropdown);
    };

    const handleUpdateEdnStatus = async (newStatus) => {
        if (!newStatus || updatingEdnStatus || newStatus === ticket?.status) return;

        setUpdatingEdnStatus(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.put('/api/edn_tms/edn_tms/ticket/status',
                { ticket_id: ticketId, status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setTicket(prev => prev ? { ...prev, status: newStatus } : prev);
            setShowEdnStatusDropdown(false);
            setOverlayState({
                show: true,
                message: t('messageWindow.statusUpdateSuccess', 'Status updated successfully'),
                success: true
            });
        } catch (error) {
            const errorMessage = error.response?.data?.detail || t('messageWindow.statusUpdateError', 'Failed to update status');
            setOverlayState({
                show: true,
                message: errorMessage,
                success: false
            });
        } finally {
            setUpdatingEdnStatus(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
        }
    };

    const fetchAvailableAssistants = async () => {
        setAssistantsLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get('/api/ai-assistants', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAvailableAssistants(response.data || []);
        } catch (error) {
            console.error('Failed to fetch AI assistants:', error);
        } finally {
            setAssistantsLoading(false);
        }
    };

    const fetchCarriersConversation = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/carriers_conversation/${ticketId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCarriersConversation(response.data);
        } catch (error) {
        }
    };

    const fetchWarehouseConversation = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/warehouse_conversation/${ticketId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setWarehouseConversation(response.data);
        } catch (error) {
        } finally {
            setWarehouseLoading(false);
        }
    };

    const connectWebSocket = () => {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        // Close any previous socket before opening a new one: a leaked
        // handler that keeps reconnecting stacks live connections server-side
        // (one dashboard was observed holding 15+ sockets on one worker).
        if (wsRef.current) {
            wsRef.current.onclose = null;
            wsRef.current.close();
        }

        const ws = new WebSocket(`/api/ws?token=${token}`);
        wsRef.current = ws;

        ws.onopen = (event) => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        ws.onclose = (event) => {
            // Reconnect only if this socket is still the active one — after
            // unmount or replacement the ref no longer points at it.
            if (wsRef.current !== ws) return;
            // The server closes with 1008 when the token is expired/invalid:
            // retrying every 5 s with the same token can never succeed (one
            // stale tab produced ~100 rejected handshakes in 12 minutes on
            // 2026-09-04). Same handling as Dashboard.js: drop the session.
            const reason = (event.reason || "").toLowerCase();
            if (event.code === 1008 || event.code === 4000 || event.code === 4001 ||
                reason.includes("token") || reason.includes("auth")) {
                console.log("WebSocket closed due to authentication issue, not reconnecting");
                localStorage.removeItem("access_token");
                navigate("/");
                return;
            }
            reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
        };

        ws.onmessage = (event) => {
            if (event.data === 'ping') {
                ws.send('pong');
            } else {
                // Try to parse as JSON for presence updates
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'ticket_presence_update') {
                        // Presence updates are handled separately, no action needed here
                        // The Dashboard handles updating the ticket list presence
                        return;
                    }
                    // Ticket-scoped events (ticket_created / ticket_updated /
                    // message_created carry ticket_id): only the open ticket's
                    // conversation needs refetching. Every tenant-wide event used
                    // to trigger a full messages fetch in every open window.
                    if (data.ticket_id && ticketIdRef.current && String(data.ticket_id) !== String(ticketIdRef.current)) {
                        return;
                    }
                } catch (e) {
                    // Not JSON, treat as regular refresh message
                }
                // Fetch messages using the latest ticket context
                fetchMessagesRef.current?.();
            }
        };
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
                setShowActionMenu(false);
            }
            if (assignmentDropdownRef.current && !assignmentDropdownRef.current.contains(event.target)) {
                setShowAssignmentDropdown(false);
            }
            if (attachmentsDropdownRef.current && !attachmentsDropdownRef.current.contains(event.target)) {
                setShowAttachmentsDropdown(false);
            }
            if (topicDropdownRef.current && !topicDropdownRef.current.contains(event.target)) {
                setShowTopicDropdown(false);
            }
            if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target)) {
                setShowClientDropdown(false);
            }
            if (ednStatusDropdownRef.current && !ednStatusDropdownRef.current.contains(event.target)) {
                setShowEdnStatusDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const initialLoad = async () => {
            await fetchUserData(); // Fetches and sets user
            // Other non-ticket specific initial fetches can go here if any
        };
        initialLoad();

        // WebSocket and ping interval setup will be handled by the useEffect below, dependent on 'user'
        
        return () => {
            // General cleanup for the component if any, not specific to WebSocket anymore here
        };
    }, []); // Runs once on component mount

    // New useEffect to handle WebSocket connection based on user or ticket
    useEffect(() => {
        if (user) {
            connectWebSocket();
            pingIntervalRef.current = setInterval(() => {
                if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                    wsRef.current.send('ping');
                }
            }, 30000);
        }

        return () => {
            // Cleanup WebSocket and ping interval if they were initialized
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [user, ticketId]); // Runs when 'user' or 'ticketId' state changes

    // Add new useEffect to fetch employees when user is available
    useEffect(() => {
        if (user) {
            fetchEmployees();
        }
    }, [user]);

    // Presence management - drives "who is viewing" + "time spent on ticket" metric.
    // Open once for the current ticket, refresh with heartbeats, and only close on
    // real exits so rerenders do not create artificial visit sessions.
    useEffect(() => {
        if (!ticketId || !user?._id) return;

        const token = localStorage.getItem('access_token');
        if (!token) return;

        if (presenceCloseTimeoutsRef.current[ticketId]) {
            clearTimeout(presenceCloseTimeoutsRef.current[ticketId]);
            delete presenceCloseTimeoutsRef.current[ticketId];
        }

        const markPresence = async () => {
            try {
                await axios.post(
                    `/api/tickets/${ticketId}/presence`,
                    { is_active: true },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } catch (error) {
                console.error('Failed to mark presence:', error);
            }
        };

        const startHeartbeat = () => {
            if (presenceIntervalRef.current) return;
            presenceIntervalRef.current = setInterval(markPresence, 60000);
        };

        const stopHeartbeat = () => {
            if (presenceIntervalRef.current) {
                clearInterval(presenceIntervalRef.current);
                presenceIntervalRef.current = null;
            }
        };

        const clearPresence = async () => {
            try {
                await axios.delete(`/api/tickets/${ticketId}/presence`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch (error) {
                console.error('Failed to clear presence:', error);
            }
        };

        const schedulePresenceClose = () => {
            if (presenceCloseTimeoutsRef.current[ticketId]) {
                clearTimeout(presenceCloseTimeoutsRef.current[ticketId]);
            }

            const timeoutId = setTimeout(() => {
                clearPresence();
                delete presenceCloseTimeoutsRef.current[ticketId];
            }, 1000);

            presenceCloseTimeoutsRef.current[ticketId] = timeoutId;
        };

        // sendBeacon does not support DELETE and cannot attach Authorization headers.
        // POST {is_active:false} is the documented fallback for tab-close / reload.
        const beaconClose = () => {
            try {
                const blob = new Blob(
                    [JSON.stringify({ is_active: false })],
                    { type: 'application/json' }
                );
                navigator.sendBeacon(`/api/tickets/${ticketId}/presence`, blob);
            } catch (error) {
                console.error('Failed to beacon-close presence:', error);
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                stopHeartbeat();
            } else if (document.visibilityState === 'visible') {
                markPresence();
                startHeartbeat();
            }
        };

        const handlePageHide = () => {
            stopHeartbeat();
            beaconClose();
            activePresenceTicketRef.current = null;
        };

        markPresence();
        activePresenceTicketRef.current = ticketId;
        startHeartbeat();

        window.addEventListener('pagehide', handlePageHide);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            stopHeartbeat();
            window.removeEventListener('pagehide', handlePageHide);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            // SPA route change: close ticket A before ticket B mounts.
            schedulePresenceClose();
            if (activePresenceTicketRef.current === ticketId) {
                activePresenceTicketRef.current = null;
            }
        };
    }, [ticketId, user?._id]);

    // This useEffect will run when ticketId changes, or when the user state is updated
    // (e.g., after fetchUserData completes).
    // Uses ref to prevent duplicate fetches for the same ticket
    useEffect(() => {
        if (user !== undefined && ticketId) { // user can be null (failed login) or an object
            // Only fetch if we haven't fetched this ticket already
            if (lastFetchedTicketIdRef.current !== ticketId) {
                lastFetchedTicketIdRef.current = ticketId;
                fetchTicketAndMessages();
            }
        }
    }, [ticketId, user]);

    const fetchUserData = async () => {
        setUserLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                 setUser(null); 
                 setUserLoading(false);
                 navigate('/'); 
                 return;
            }
            const response = await axios.get('/api/users/user', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
        } catch (error) {
            setUser(null); 
            if (error.response && (error.response.status === 401 || error.response.status === 422)) {
                localStorage.removeItem('access_token');
                navigate('/');
            }
        } finally {
            setUserLoading(false);
        }
    };

    const fetchTicketAndMessages = async () => {
        setLoading(true);
        setRelatedTickets({ previous: null, next: [] });
        const token = localStorage.getItem('access_token');

        try {
            if (!token) {
                setTicket(null);
                setMessages([]);
                setLoading(false);
                navigate('/');
                return;
            }

            const response = await axios.get(`/api/tickets/${ticketId}/messages`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data && response.data.ticket) {
                setTicket(response.data.ticket);
                setRelatedTickets({
                    previous: response.data.related_tickets?.previous || null,
                    next: response.data.related_tickets?.next || [],
                });
                setMessages(response.data.messages || []);
                setEmployee(response.data.employee || 'N/A');
                setAiAgentName(response.data.ai_agent_name || '');
                setBrand(response.data.brand || null);
                
                fetchOrderAndCustomerInfo(response.data.ticket, token);
                
                setSummary(response.data.ticket.summary || response.data.ticket.subject || 'No summary available.');
                
                // Fetch additional data in parallel for better performance
                await Promise.all([
                    fetchTicketContext(),
                    fetchCarriersConversation(),
                    user?.edn_api_account ? fetchWarehouseConversation() : Promise.resolve(),
                    fetchTicketFiles()
                ]);
            } else {
                setTicket(null);
                setMessages([]);
            }
            setLoading(false);
            
        } catch (error) {
            setTicket(null); 
            setMessages([]);
            setLoading(false);
        }
    };

    const fetchMessages = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/${ticketId}/messages`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setMessages(response.data.messages);
        } catch (error) {
        }
    };

    // Keep a ref to the latest fetchMessages so the WebSocket uses the current ticket
    useEffect(() => {
        fetchMessagesRef.current = fetchMessages;
    }, [fetchMessages]);

    const fetchOrderAndCustomerInfo = async (ticketData, token) => {
        if (ticketData.customer_id) {
            fetchCustomerInfo(ticketData.customer_id, token);
        } else if (ticketData.customer_email) {
            setClientInfo(`Email: ${ticketData.customer_email}`);
        } else {
            setClientInfo('');
        }

        if (ticketData.order) {
            fetchOrderInfo(ticketData.order, token);
        } else {
            setOrderDetails('');
        }
    };

    // New function to fetch ticket files
    const fetchTicketFiles = async () => {
        setFilesLoading(true);
        setMessageFiles({});
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/${ticketId}/files`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const ticketLevelFiles = response.data?.files || [];
            const messageFilePayload = response.data?.message_files || [];
            
            const messageFileMap = messageFilePayload.reduce((acc, entry) => {
                if (entry?.message_id && Array.isArray(entry.files)) {
                    acc[entry.message_id] = entry.files;
                }
                return acc;
            }, {});

            setTicketFiles(ticketLevelFiles);
            setMessageFiles(messageFileMap);
        } catch (error) {
            setMessageFiles({});
        } finally {
            setFilesLoading(false);
        }
    };

    const fetchClientTickets = useCallback(
        async (email) => {
            try {
                const token = localStorage.getItem('access_token');
                if (!token) {
                    if (latestClientEmailRef.current !== email) {
                        return;
                    }

                    setClientTickets([]);
                    setClientTicketsError(t('messageWindow.clientTicketsLoadError'));
                    return;
                }

                const response = await axios.get('/api/clients/tickets/by-email', {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        client_email: email,
                        limit: 20,
                    },
                });

                if (latestClientEmailRef.current !== email) {
                    return;
                }

                setClientTickets(Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                if (latestClientEmailRef.current !== email) {
                    return;
                }

                const fallbackMessage =
                    error?.response?.data?.detail || t('messageWindow.clientTicketsLoadError');

                setClientTickets([]);
                setClientTicketsError(fallbackMessage);
            } finally {
                if (latestClientEmailRef.current === email) {
                    setClientTicketsLoading(false);
                }
            }
        },
        [t]
    );

    const adjustTextareaHeight = useCallback(() => {
        if (!textareaRef.current) return;
        const textarea = textareaRef.current;
        textarea.style.height = '120px';
        const nextHeight = Math.min(Math.max(textarea.scrollHeight, 120), 300);
        textarea.style.height = `${nextHeight}px`;
    }, []);

    const handleComposerClick = useCallback((event) => {
        if (!textareaRef.current) return;
        const target = event.target;
        if (target?.closest && target.closest('.textarea-action-button')) {
            return;
        }
        textareaRef.current.focus();
    }, []);

    const getHandoffPreview = useCallback(
        (content) => {
            const normalized = (content || '').trim();
            if (!normalized) {
                return t('messageWindow.noHandoffContent', 'No handoff instructions provided.');
            }
            // Get first line only for collapsed preview
            const firstLine = normalized.split('\n')[0];
            if (firstLine.length <= 80) {
                return firstLine;
            }
            return `${firstLine.slice(0, 80)}…`;
        },
        [t]
    );

    const normalizeMessageContent = (content) => (typeof content === 'string' ? content : '');

    // Message text: one line per <br />, http(s):// and www. addresses clickable (LinkifiedText).
    const formatMessageContent = (content) => <LinkifiedText text={normalizeMessageContent(content)} />;
    
    const fetchCustomerInfo = async (customerId, token) => {
        setCustomerLoading(true);
        try {
            const response = await axios.get(`/api/shopify/get_customer`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { customer_id: customerId }
            });
            const customer = response.data;
            setClientInfo(`Phone: ${customer.phone}\nFirst Name: ${customer.first_name}\nLast Name: ${customer.last_name}\nEmail: ${customer.email}`);
        } catch (error) {
        } finally {
            setCustomerLoading(false);
        }
    };

    const fetchOrderInfo = async (orderId, token) => {
        setOrderLoading(true);
        try {
            const response = await axios.get(`/api/logistics/edn/order_by_id/${orderId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const order = response.data;
            const formattedOrderDetails = `
Order ID: ${order.order_id}
Reference: ${order.reference}
Status: ${order.status}
Total Price: ${order.grand_total} ${order.currency}
Shipping Cost: ${order.shipping_cost} ${order.currency}
Created At: ${order.created_at}
Customer Email: ${order.customer_email}
Customer Name: ${order.customer_name}
Customer Phone: ${order.customer_phone}
Shipping Address:
    ${order.shipping_address.firstname} ${order.shipping_address.lastname}
    ${order.shipping_address.street1}
    ${order.shipping_address.street2 ? order.shipping_address.street2 + '\n' : ''}
    ${order.shipping_address.postcode} ${order.shipping_address.city}
    ${order.shipping_address.country}
Line Items:
${order.items.map(item => `
Product ID: ${item.product_id}
Name: ${item.name}
SKU: ${item.sku}
Quantity: ${item.quantity}
Price: ${item.price} ${order.currency}
EAN: ${item.ean || 'N/A'}
`).join('\n')}
`;
            setOrderDetails(formattedOrderDetails);
        } catch (error) {
        } finally {
            setOrderLoading(false);
        }
    };

    const openEdnVisibilityModal = (action, messageId = null) => {
        setEdnVisibilityModal({
            show: true,
            action,
            messageId
        });
    };

    const closeEdnVisibilityModal = () => {
        setEdnVisibilityModal({
            show: false,
            action: null,
            messageId: null
        });
    };

    const handleEdnVisibilitySelect = (visibility) => {
        const pendingAction = ednVisibilityModal.action;
        const pendingMessageId = ednVisibilityModal.messageId;
        closeEdnVisibilityModal();

        if (pendingAction === 'new-message') {
            executeSendMessage(visibility);
            return;
        }

        if (pendingAction === 'draft-message' && pendingMessageId) {
            executeSendDraftMessage(pendingMessageId, visibility);
        }
    };

    const handleSendMessage = () => {
        if (!newMessage.trim() && pendingFiles.length === 0) return;
        if (sendingMessage) return;

        if (isEdnCustomUser) {
            openEdnVisibilityModal('new-message');
            return;
        }

        executeSendMessage('all');
    };

    const executeSendMessage = async (visibility = 'all') => {
        if (!newMessage.trim() && pendingFiles.length === 0) return;
        if (sendingMessage) return;
        
        setSendingMessage(true);
        try {
            const token = localStorage.getItem('access_token');
            
            const payload = {
                ticket_id: ticketId,
                content: newMessage,
            };

            if (isEdnCustomUser) {
                payload.visibility = visibility;
            }
            
            // Include files if any are pending
            if (pendingFiles.length > 0) {
                payload.files = pendingFiles.map(f => ({
                    name: f.name,
                    mime_type: f.mime_type,
                    content: f.content
                }));
            }
            
            await axios.post('/api/messages/add', payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            
            setNewMessage('');
            setPendingFiles([]); // Clear pending files after successful send
            fetchMessages();
            
            // Refresh files list if files were sent
            if (pendingFiles.length > 0) {
                fetchTicketFiles();
            }
        } catch (error) {
            setOverlayState({
                show: true,
                message: t('messageWindow.sendError', 'Failed to send message'),
                success: false
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
        } finally {
            setSendingMessage(false);
        }
    };

    const handleGetAssistantResponse = async () => {
        setGettingAiHelp(true);
        try {
            const token = localStorage.getItem('access_token');
            const aiLogsString = JSON.stringify(ticket.ai_logs);

            const response = await axios.post('/api/messages/writer_assistant', {
                text: newMessage,
                ai_logs: aiLogsString,
                ticket_id: ticketId,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            // KI-84: when the assistant could not produce a message (it answered
            // with commentary instead), the backend returns the draft unchanged
            // with rewritten=false — keep the operator's text and say so.
            if (response.data.rewritten === false) {
                setOverlayState({
                    show: true,
                    message: t('messageWindow.aiHelpKept', 'The assistant could not rewrite this draft; your text was kept.'),
                    success: false,
                });
                setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 4000);
                return;
            }
            setNewMessage(response.data.response);
        } catch (error) {
            setOverlayState({
                show: true,
                message: t('messageWindow.aiHelpFailed', 'The writing assistant is unavailable; your draft was kept.'),
                success: false,
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 4000);
        } finally {
            setGettingAiHelp(false);
        }
    };

    const redirectWithNotification = (message, success) => {
        const storedSearch = sessionStorage.getItem('una_dashboard_search');
        const dashboardSearch = location.state?.fromDashboardSearch
            || storedSearch
            || '';
        if (storedSearch) {
            sessionStorage.removeItem('una_dashboard_search');
        }
        navigate(`/dashboard${dashboardSearch}`, {
            state: {
                notification: {
                    message,
                    success
                }
            }
        });
    };

    const handleResolveTicket = async () => {
        try {
            const token = localStorage.getItem('access_token');
            await axios.put(`/api/tickets/${ticketId}/resolve`, null, {
                headers: { Authorization: `Bearer ${token}` },
            });
            redirectWithNotification(t('messageWindow.resolveSuccess'), true);
        } catch (error) {
            redirectWithNotification(t('messageWindow.resolveError'), false);
        }
    };
    
    // The stored summary first (28/10): shown when current and in the person's
    // language, generated (and stored) otherwise or on « Regenerate ».
    const handleGetTicketSummary = async (regenerate = false) => {
        setShowSummaryModal(true);
        setLoadingSummary(true);
        setTicketSummary('');
        try {
            const stored = regenerate === true ? null : await fetchStoredSummary(ticketId).catch(() => null);
            const current = stored && stored.summary && !summaryNeedsUpdate(stored)
                ? stored : await generateSummary(ticketId);
            setTicketSummary(current.summary || '');
        } catch (error) {
            setOverlayState({
                show: true,
                message: t('messageWindow.summaryError', 'Failed to generate summary'),
                success: false
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
            setShowSummaryModal(false);
        } finally {
            setLoadingSummary(false);
        }
    };

    const getEdnStatusLabel = (statusValue) => {
        if (!statusValue) return t('messageWindow.unknownStatus', 'Unknown');
        const statusOption = EDN_STATUS_MAPPING.find((status) => status.value === statusValue);
        return statusOption?.name || getStatusLabel(t, statusValue);
    };

    const handleGetStatusHistory = async () => {
        if (!isEdnCustomUser || loadingStatusHistory) return;

        setShowStatusHistoryModal(true);
        setLoadingStatusHistory(true);
        setStatusHistoryData(null);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/edn_tms/edn_tms/ticket/${ticketId}/status-history`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setStatusHistoryData(response.data);
        } catch (error) {
            const errorMessage = error.response?.data?.detail || t('messageWindow.statusHistoryError', 'Failed to load status history');
            setOverlayState({
                show: true,
                message: errorMessage,
                success: false
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
            setShowStatusHistoryModal(false);
        } finally {
            setLoadingStatusHistory(false);
        }
    };

    const handleSendDraftMessage = (messageId) => {
        if (sendingDraftMessageId === messageId) return;

        if (isEdnCustomUser) {
            openEdnVisibilityModal('draft-message', messageId);
            return;
        }

        executeSendDraftMessage(messageId, 'all');
    };

    // The carrier contact the AI prepared with this reply will not leave with it
    // (refused, unticked, failed…): the reply may announce it (backend 409,
    // draft_carrier_contacts.contacts_not_leaving). The operator confirms first.
    const confirmReplyWithoutCarrierContacts = (detail) => {
        const contacts = Array.isArray(detail?.contacts) ? detail.contacts : [];
        const lines = contacts.map((contact) => `${contact.carrier_name || '?'} : ${t(
            `messageWindow.carrierContactNotLeaving.${contact.reason}`, contact.reason || '')}`);
        return window.confirm(t('messageWindow.carrierContactNotLeavingConfirm',
            'This AI reply was written with the carrier contact prepared in the same run, which will not leave: {{contacts}}. The reply may announce it to the customer. Check it (edit it if needed). Send the reply without this contact?',
            { contacts: lines.join(' · ') }));
    };

    const executeSendDraftMessage = async (messageId, visibility = 'all', withoutCarrierContacts = false) => {
        setSendingDraftMessageId(messageId);
        try {
            const token = localStorage.getItem('access_token');
            const payload = { message_id: messageId };

            if (isEdnCustomUser) {
                payload.visibility = visibility;
            }
            payload.send_carrier_contacts = !skipDraftCarrierContacts[messageId];
            if (withoutCarrierContacts) {
                payload.confirm_without_carrier_contacts = true;
            }

            const filesForDraft = draftPendingFiles[messageId];
            if (filesForDraft && filesForDraft.length > 0) {
                payload.files = filesForDraft.map(f => ({
                    name: f.name,
                    mime_type: f.mime_type,
                    content: f.content
                }));
            }

            const sendResponse = await axios.post(`/api/messages/send`, payload, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            reportDraftCarrierContacts(sendResponse?.data?.carrier_contacts);

            setDraftPendingFiles(prev => {
                const updated = { ...prev };
                delete updated[messageId];
                return updated;
            });

            fetchMessages();
            // The send has already approved the draft's carrier contacts
            // server-side (KI-108): reload the carrier panel too, or it keeps
            // « Approuver / Rejeter » on a contact already sent (KI-184).
            fetchCarriersConversation();
            if (filesForDraft && filesForDraft.length > 0) {
                fetchTicketFiles();
            }
        } catch (error) {
            const detail = error?.response?.data?.detail;
            if (error?.response?.status === 409 && detail?.code === 'carrier_contact_not_leaving') {
                // Nothing was sent: the operator decides, then the same send again.
                fetchCarriersConversation();
                if (confirmReplyWithoutCarrierContacts(detail)) {
                    await executeSendDraftMessage(messageId, visibility, true);
                }
                return;
            }
            setOverlayState({
                show: true,
                message: t('messageWindow.sendError', 'Failed to send message'),
                success: false
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        } finally {
            setSendingDraftMessageId(null);
        }
    };

    // Outcome of the carrier contacts sent with an approved draft (KI-108).
    const reportDraftCarrierContacts = (outcomes) => {
        if (!Array.isArray(outcomes) || outcomes.length === 0) return;
        const names = (items) => items.map((item) => item.carrier_name || '?').join(', ');
        const sent = outcomes.filter((item) => item.sent);
        const notSent = outcomes.filter((item) => !item.sent);
        const message = notSent.length > 0
            ? t('messageWindow.carrierContactsNotSent', 'Reply sent · carrier contact NOT sent ({{carriers}}): {{error}}', {
                carriers: names(notSent),
                error: notSent.map((item) => (item.error === 'failed_contact'
                    ? t('messageWindow.draftCarrierContactsFailed', 'Carrier contact failed, not sent with this reply — fix it in the carrier panel: {{carriers}}', { carriers: item.carrier_name || '?' })
                    : item.error)).filter(Boolean).join(' · ')
            })
            : t('messageWindow.carrierContactsSent', 'Reply sent · carrier contact sent: {{carriers}}', { carriers: names(sent) });
        setOverlayState({ show: true, message, success: notSent.length === 0 });
        setTimeout(() => setOverlayState({ show: false, message: '', success: false }), notSent.length ? 7000 : 3000);
    };

    const handleDeleteDraftMessage = async (messageId) => {
        try {
            const token = localStorage.getItem('access_token');
            await axios.post(`/api/messages/delete`, { message_id: messageId }, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            
            // Immediately remove the message from local state for better UX
            setMessages(prevMessages => prevMessages.filter(message => message._id !== messageId));
        } catch (error) {
            // Optionally show an error message to the user
            setOverlayState({ 
                show: true, 
                message: t('messageWindow.deleteError', 'Failed to delete message'), 
                success: false 
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        }
    };

    const handleModifyDraftMessage = (message) => {
        setEditingMessage(message);
        setEditedContent(message.content);
    };

    const formatDate = (dateString) => {
        const options = { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit',
            timeZone: 'Europe/Paris'
        };
        // Naive backend strings are UTC (shared parser, KI-110).
        const parsed = parseUtc(dateString);
        return parsed ? parsed.toLocaleDateString('fr-FR', options) : '';
    };

    const handleSaveModifiedMessage = async () => {
        try {
            const token = localStorage.getItem('access_token');
            await axios.post(`/api/messages/edit`, {
                message_id: editingMessage._id,
                content: editedContent,
            }, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            setEditingMessage(null);
            setEditedContent('');
            fetchMessages(); // Fetch messages to update the message list
        } catch (error) {
        }
    };

    const handleCancelModification = () => {
        setEditingMessage(null);
        setEditedContent('');
    };

    // File overlay functions
    const handleFileClick = (file) => {
        if (!file) return;
        const targetIndex = attachmentList.findIndex((attachment) => attachment.file === file);
        if (targetIndex !== -1) {
            setSelectedAttachmentIndex(targetIndex);
        } else {
            setSelectedAttachmentIndex(null);
        }
    };

    const closeFileOverlay = () => {
        setSelectedAttachmentIndex(null);
    };

    const handleAttachmentNavigation = (step) => {
        if (selectedAttachmentIndex === null) return;
        const nextIndex = selectedAttachmentIndex + step;
        if (nextIndex < 0 || nextIndex >= attachmentList.length) return;
        setSelectedAttachmentIndex(nextIndex);
    };

    const fetchEmployees = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get('/api/employees/agents/basic', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setEmployees(response.data);
        } catch (error) {
        }
    };

    const handleAssignTicket = async (employeeId) => {
        if (!ticketId) {
            setOverlayState({ 
                show: true, 
                message: t('messageWindow.assignmentError'), 
                success: false 
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
            return;
        }

        setAssigningTicket(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.post('/api/employees/assign-employee', {
                employee_id: employeeId,
                ticket_id: ticketId
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            
            setOverlayState({ 
                show: true, 
                message: t('messageWindow.assignmentSuccess'), 
                success: true 
            });
            setShowAssignmentDropdown(false);
            
            // Refresh ticket data to show updated assignment
            await fetchTicketAndMessages();
        } catch (error) {
            setOverlayState({ 
                show: true, 
                message: t('messageWindow.assignmentError'), 
                success: false 
            });
        } finally {
            setAssigningTicket(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        }
    };

    useEffect(() => {
        if (!userLoading && !user) {
            navigate('/');
        }
    }, [userLoading, user, navigate]);

    const recipientDetails = contextData?.recipient_info ?? {};
    const clientDetails = contextData?.client_info ?? {};
    const orderDetailsData = contextData?.order_info ?? {};
    const shippingDetails = contextData?.shipping_info ?? {};
    const compatibilityDetails = contextData?.compatibility_result;
    const normalizedClientEmail = (clientDetails.email_client || ticket?.customer_email || '')
        .trim()
        .toLowerCase();
    const compatibilityPartList = Array.isArray(compatibilityDetails?.parts) ? compatibilityDetails.parts : [];
    const compatibilityMetaEntries = compatibilityDetails
        ? Object.entries(compatibilityDetails).filter(
            ([key, value]) => key !== 'parts' && value !== null && value !== undefined && value !== ''
        )
        : [];
    const compatibilitySerialNumber =
        compatibilityDetails?.serial_number ||
        compatibilityDetails?.serialNumber ||
        contextData?.device_info?.serial_number ||
        contextData?.device_info?.serialNumber ||
        null;

    useEffect(() => {
        if (!normalizedClientEmail) {
            latestClientEmailRef.current = '';
            setClientTickets([]);
            setClientTicketsError(null);
            setClientTicketsLoading(false);
            return;
        }

        latestClientEmailRef.current = normalizedClientEmail;
        setClientTicketsLoading(true);
        setClientTicketsError(null);
        fetchClientTickets(normalizedClientEmail);
    }, [normalizedClientEmail, fetchClientTickets, ticketId]);

    useEffect(() => {
        adjustTextareaHeight();
    }, [newMessage, adjustTextareaHeight]);

    useEffect(() => {
        if (selectedAttachmentIndex === null) return;
        if (attachmentList.length === 0) {
            setSelectedAttachmentIndex(null);
            return;
        }
        if (selectedAttachmentIndex > attachmentList.length - 1) {
            setSelectedAttachmentIndex(attachmentList.length - 1);
        }
    }, [attachmentList.length, selectedAttachmentIndex]);

    const formatCompatibilityValue = (value) => {
        if (typeof value === 'boolean') {
            return value ? t('messageWindow.yes', 'Yes') : t('messageWindow.no', 'No');
        }
        if (value && typeof value === 'object') {
            try {
                return JSON.stringify(value, null, 2);
            } catch (error) {
                return String(value);
            }
        }
        return value;
    };

    const getCleanPartName = useCallback((part) => {
        const name = part?.part_name || '';
        const pn = part?.part_number || '';
        if (name && pn && name.includes(pn)) {
            const cleaned = name.replace(pn, '').replace(/\s{2,}/g, ' ').trim().replace(/[-–—]+$/, '').trim();
            return cleaned || name;
        }
        return name;
    }, []);

    const getPartSku = useCallback((part) => part?.sku || part?.SKU || null, []);

    if (loading) {
        return <div>{t('messageWindow.loading')}</div>;
    }

    if (!ticket) {
        return <div>{t('messageWindow.failedToLoadTicket')}</div>;
    }

    const isEditable = !TERMINAL_READ_STATUS_VALUES.includes(ticket.status);
    const storedUserData = getStoredUserData();
    const isEdnCustomUser = user?.custom_user === 'edn'
        || user?.user_data?.custom_user === 'edn'
        || storedUserData?.custom_user === 'edn';
    const canDisplayColissimoLabels = Boolean(user) && !isEdnCustomUser;
    const ednStatusOptions = availableEdnStatuses.length > 0 ? availableEdnStatuses : EDN_STATUS_MAPPING;
    const currentEdnStatus = ednStatusOptions.find((statusOption) => statusOption.value === ticket.status);
    const ticketStatusLabel = currentEdnStatus?.name || getStatusLabel(t, ticket.status);
    const currentTopicId = ticket.ticket_topic_id
        || ticket.topic_id
        || ticket.ticket_topic?.id
        || contextData?.shipping_info?.topic_id;
    const currentTopicValue = contextData?.shipping_info?.topic || ticket?.topic;
    const currentTopicLabel = ticket.ticket_topic?.topic_full
        || ticket.ticket_topic?.name
        || ticket.topic_name
        || contextData?.shipping_info?.topic_name
        || currentTopicValue
        || t('messageWindow.noTopic', 'No topic');
    
    const handleLanguageChange = async (language) => {
        setTargetLanguage(language);
        if (language !== 'original') {
            setIsTranslating(true);
            await translateMessages(language);
            setIsTranslating(false);
        } else {
            setTranslatedMessages({});
        }
    };

    const translateMessages = async (language) => {
        try {
            const token = localStorage.getItem('access_token');
            
            // Filter out messages that shouldn't be translated (AI actions, instructions, or messages without content)
            const translatableMessages = messages.filter(m =>
                !m.is_instructions &&
                m.direction !== 'is_action' &&
                m.direction !== 'is_action_result' &&
                m.status !== 'internal' &&
                m.content &&
                m.content.trim() !== ''
            );
            
            if (translatableMessages.length === 0) {
                // No messages to translate
                return;
            }
            
            const response = await axios.post('/api/messages/translate_messages', {
                messages: translatableMessages.map(m => ({ id: m._id, content: m.content })),
                target_language: language
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const translatedObj = {};
            response.data.translated_messages.forEach(m => {
                translatedObj[m.id] = m.content;
            });
            setTranslatedMessages(translatedObj);
        } catch (error) {
            console.error('Translation error:', error);
        }
    };

    const renderMessageContent = (message) => {
        const content = targetLanguage === 'original' ? message.content : (translatedMessages[message._id] || message.content);
        return formatMessageContent(content);
    };

    const handleTextareaChange = (event) => {
        setNewMessage(event.target.value);
        adjustTextareaHeight();
    };

    const handleCopyTracking = () => {
        const tracking = contextData?.shipping_info?.master_tracking;
        if (!tracking || !navigator.clipboard) return;
        navigator.clipboard.writeText(tracking)
            .then(() => {
                setTrackingCopied(true);
                setTimeout(() => setTrackingCopied(false), 2000);
            })
            .catch(() => {});
    };

    // Toggle AI action expansion
    const toggleAiActionExpansion = (actionId) => {
        setExpandedAiActions(prev => ({
            ...prev,
            [actionId]: !prev[actionId]
        }));
    };

    // Updated AI action rendering to show result status instead of function name
    const renderAiAction = (message) => {
        const isAction = message.direction === 'is_action';
        const isActionResult = message.direction === 'is_action_result';
        const isExpanded = expandedAiActions[message._id];
        
        // Extract assign_to_employee summary for quick preview
        let assignmentSummary = null;
        let assignmentEmail = null;
        if (isAction && message.function_call?.name === 'assign_to_employee') {
            try {
                const args = typeof message.function_call.arguments === 'string' 
                    ? JSON.parse(message.function_call.arguments)
                    : message.function_call.arguments;
                assignmentSummary = args?.summary || null;
                assignmentEmail = args?.email || null;
            } catch (e) {
                // Parsing failed, no summary to show
            }
        }
        
        return (
            <div key={message._id} className={`ai-action ${isAction ? 'action' : 'action-result'} ${isExpanded ? 'expanded' : ''}`}>
                <div className="ai-action-header">
                    <div className="ai-action-content">
                        {isAction && message.function_call && (
                            <span className="ai-function-call">
                                <FaRobot />
                                {t('messageWindow.aiActionLabel', 'Action IA')} · {message.function_call.name.replace(/_/g, ' ')}
                            </span>
                        )}
                        {isActionResult && (() => {
                            let status = 'completed';
                            try {
                                const result = JSON.parse(message.content?.replace(/'/g, '"') || message.function_result?.replace(/'/g, '"') || '{}');
                                status = result.status || 'completed';
                            } catch (e) {
                                // Keep the "completed" fallback when the payload is not valid JSON
                            }
                            const isOk = status === 'success' || status === 'completed';
                            const isError = status === 'error';
                            return (
                                <span className={`ai-function-result ${isOk ? 'success' : ''} ${isError ? 'error' : ''}`}>
                                    {isOk ? <FaCheckCircle /> : isError ? <FaTimesCircle /> : <FaInfoCircle />}
                                    {isOk
                                        ? t('messageWindow.aiActionDone', 'Terminé')
                                        : isError
                                            ? t('messageWindow.aiActionFailed', 'Échec')
                                            : status}
                                </span>
                            );
                        })()}
                    </div>
                    {message.run_id && (
                        <button
                            type="button"
                            className="ai-action-run-link"
                            onClick={() => openAiRun(message.run_id, message.tool_span_id || message.span_id)}
                            title={t('messageWindow.viewAiRunTitle', 'Voir la trace IA qui a produit ce message')}
                        >
                            <FaProjectDiagram />
                        </button>
                    )}
                    <button
                        className="ai-action-toggle"
                        onClick={() => toggleAiActionExpansion(message._id)}
                        title={isExpanded ? t('messageWindow.hideDetails') : t('messageWindow.showDetails')}
                    >
                        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                </div>
                
                {/* Assignment summary preview - always visible for assign_to_employee */}
                {assignmentSummary && (
                    <div className="ai-action-assignment-preview">
                        <div className="assignment-preview-label">
                            <FaUserPlus /> {t('messageWindow.assignedTo', 'Assigné à')} {assignmentEmail ? assignmentEmail.split('@')[0] : ''}
                        </div>
                        <div className="assignment-preview-summary">
                            {assignmentSummary}
                        </div>
                    </div>
                )}
                
                {isExpanded && (
                    <div className="ai-action-details">
                        {isAction && message.function_call && (
                            <>
                                <div className="ai-action-detail-section">
                                    <strong className="ai-action-label">{t('messageWindow.functionName')}:</strong>
                                    <div className="ai-action-value">{message.function_call.name}</div>
                                </div>
                                {message.function_call.arguments && (
                                    <div className="ai-action-detail-section">
                                        <strong className="ai-action-label">{t('messageWindow.arguments')}:</strong>
                                        <pre className="ai-action-json">{
                                            typeof message.function_call.arguments === 'string' 
                                                ? JSON.stringify(JSON.parse(message.function_call.arguments), null, 2)
                                                : JSON.stringify(message.function_call.arguments, null, 2)
                                        }</pre>
                                    </div>
                                )}
                            </>
                        )}
                        {isActionResult && (
                            <>
                                <div className="ai-action-detail-section">
                                    <strong className="ai-action-label">{t('messageWindow.functionName')}:</strong>
                                    <div className="ai-action-value">{message.function_name}</div>
                                </div>
                                {(message.function_result || message.content) && (
                                    <div className="ai-action-detail-section">
                                        <strong className="ai-action-label">{t('messageWindow.result')}:</strong>
                                        <pre className="ai-action-json">{
                                            (() => {
                                                try {
                                                    const resultString = message.function_result || message.content;
                                                    const parsed = JSON.parse(resultString.replace(/'/g, '"'));
                                                    return JSON.stringify(parsed, null, 2);
                                                } catch (e) {
                                                    return message.function_result || message.content;
                                                }
                                            })()
                                        }</pre>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Toggle note expansion for a specific message
    const toggleNoteExpansion = (messageId) => {
        setExpandedNotes(prev => ({
            ...prev,
            [messageId]: !(prev[messageId] ?? true)
        }));
    };

    // Add new function to render note cards
    // A message's notes, open by default and aligned with their message (an
    // AI draft or a sent reply on the right, a customer message on the left).
    const renderNoteCard = (message) => {
        if (!message.notes || message.notes.length === 0) return null;

        const isExpanded = expandedNotes[message._id] ?? true;
        const noteCount = message.notes.length;
        const isOutgoing = message.sender === 'employee' || ['sent', 'draft', 'superseded'].includes(message.status);

        return (
            <div key={`notes-${message._id}`}
                className={`note-card ${isOutgoing ? 'note-card--outgoing' : 'note-card--incoming'} ${isExpanded ? 'expanded' : 'collapsed'}`}>
                <button 
                    type="button"
                    className="note-card-header"
                    onClick={() => toggleNoteExpansion(message._id)}
                    aria-expanded={isExpanded}
                >
                    <div className="note-card-header-left">
                        <FaStickyNote className="note-card-icon" />
                        <span className="note-card-title">
                            {t('messageWindow.notes')}
                        </span>
                        <span className="note-card-count">{noteCount}</span>
                    </div>
                    <div className="note-card-toggle">
                        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                </button>
                <div className={`note-card-content ${isExpanded ? 'visible' : ''}`}>
                    {message.notes.map((note, index) => (
                        <div key={index} className="note-item">
                            <div className="note-content"><LinkifiedText text={note.content} /></div>
                            <div className="note-meta">
                                {note.added_by && <span className="note-author">{note.added_by}</span>}
                                <span className="note-date">{formatNoteDate(note.added_at)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderMessageAttachments = (messageId) => {
        const filesForMessage = messageFiles[messageId];
        if (!filesForMessage || filesForMessage.length === 0) {
            return null;
        }

        const decorativeFiles = filesForMessage.filter((file) => isDecorativeFile(file));
        const showDecorative = Boolean(expandedDecorative[messageId]);
        const visibleFiles = filesForMessage.filter((file) => showDecorative || !isDecorativeFile(file));

        return (
            <div className="message-attachments">
                {visibleFiles.map((file, index) => (
                    <button
                        type="button"
                        key={`${messageId}-${file?.metadata?.file_id || file?.name || index}`}
                        className={`message-attachment ${getFileClass(file?.mime_type || '')}${isDecorativeFile(file) ? ' message-attachment--decorative' : ''}`}
                        onClick={() => handleFileClick(file)}
                        title={attachmentTooltip(file)}
                    >
                        <span className="message-attachment-icon">
                            {getFileIcon(file?.mime_type || '')}
                        </span>
                        <span className="message-attachment-name">
                            {file?.reading?.title || file?.name || t('messageWindow.attachment', 'Pièce jointe')}
                        </span>
                    </button>
                ))}
                {decorativeFiles.length > 0 && (
                    <button
                        type="button"
                        className="message-attachment message-attachment--decorative-toggle"
                        onClick={() => setExpandedDecorative((prev) => ({ ...prev, [messageId]: !prev[messageId] }))}
                        title={t('messageWindow.decorativeFileHint', 'Signature, logo or icon of an e-mail: never read by the AI nor sent to a carrier.')}
                        aria-expanded={showDecorative}
                    >
                        <span className="message-attachment-name">
                            {t('messageWindow.decorativeFiles', 'Signature images ({{count}})', { count: decorativeFiles.length })}
                        </span>
                    </button>
                )}
            </div>
        );
    };

    // KI-229: an e-mail shows what its sender wrote; the earlier mails their
    // client quoted below it (``quoted_history``) stay one click away.
    const renderQuotedHistory = (message) => {
        const quoted = typeof message.quoted_history === 'string' ? message.quoted_history.trim() : '';
        if (!quoted) {
            return null;
        }
        const isOpen = Boolean(expandedQuotedHistory[message._id]);
        return (
            <div className="message-quoted-history">
                <button
                    type="button"
                    className="message-quoted-history-toggle"
                    onClick={() => setExpandedQuotedHistory((prev) => ({ ...prev, [message._id]: !prev[message._id] }))}
                    title={t('messageWindow.quotedHistoryHint', "Earlier e-mails the sender's mail client copied below this message")}
                    aria-expanded={isOpen}
                >
                    {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                    <span>
                        {isOpen
                            ? t('messageWindow.hideQuotedHistory', 'Hide history')
                            : t('messageWindow.showQuotedHistory', 'Show history')}
                    </span>
                </button>
                {isOpen && (
                    <div className="message-quoted-history-content">
                        <LinkifiedText text={quoted} />
                    </div>
                )}
            </div>
        );
    };

    const toggleHandoffMessage = (messageId) => {
        setExpandedHandoffMessages((prev) => ({
            ...prev,
            [messageId]: !prev[messageId],
        }));
    };

    const renderMessage = (message) => {
        // Check if this is an AI action
        if (message.direction === 'is_action' || message.direction === 'is_action_result') {
            return renderAiAction(message);
        }

        // Internal AI activity record: actions taken by the AI without replying
        // to the customer (e.g. assignment before pass_response). Never sent.
        if (message.status === 'internal') {
            return (
                <div key={message._id} className="message-internal-activity">
                    <div className="internal-activity-header">
                        <FaInfoCircle />
                        <span>{t('messageWindow.aiInternalActivity', 'AI action (no reply sent)')}</span>
                    </div>
                    <div className="internal-activity-content">
                        {formatMessageContent(message.content || '')}
                    </div>
                    <div className="internal-activity-timestamp">
                        {formatDate(message.created_at)}
                    </div>
                </div>
            );
        }

        // Check if this is an instruction message
        if (message.is_instructions) {
            const isExpanded = !!expandedHandoffMessages[message._id];
            return (
                <div key={message._id} className={`message-instruction ${isExpanded ? 'expanded' : 'collapsed'}`}>
                    <div className="instruction-header">
                        <div className="instruction-icon">
                            <FaInfoCircle />
                        </div>
                        <button
                            type="button"
                            className="instruction-toggle-button"
                            onClick={() => toggleHandoffMessage(message._id)}
                            aria-expanded={isExpanded}
                        >
                            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                            <span>
                                {isExpanded
                                    ? t('messageWindow.hideHandoffDetails', 'Hide full instructions')
                                    : t('messageWindow.showHandoffDetails', 'Show full instructions')}
                            </span>
                        </button>
                    </div>
                    <div className={`instruction-content ${isExpanded ? 'instruction-content-expanded' : ''}`}>
                        {isExpanded ? (
                            renderMessageContent(message)
                        ) : (
                            <span className="instruction-preview">
                                {getHandoffPreview(message.content)}
                            </span>
                        )}
                    </div>
                    {isExpanded && (
                        <div className="instruction-timestamp">
                            {formatDate(message.created_at)}
                        </div>
                    )}
                </div>
            );
        }

        // Regular message rendering
        const isSent = message.sender === 'employee' || message.status === 'sent' || message.status === 'draft' || message.status === 'superseded';
        const ednMessageVisibility = (message.metadata?.visibility || '').toLowerCase();
        const shouldShowEdnVisibilityBadge = isEdnCustomUser
            && isSent
            && ['all', 'admin'].includes(ednMessageVisibility);
        // The team wrote it outside Unanimity (Outlook, an external ticketing
        // system, a provider's tool): coloured apart from what was sent here.
        const isTeamOutside = message.origin_kind === 'team_outside';
        const messageClass = `message ${isSent ? 'sent' : 'received'} ${message.status} ${shouldShowEdnVisibilityBadge ? 'with-edn-visibility' : ''} ${isTeamOutside ? 'origin-team-outside' : ''}`;
        
        // Check if this message is being edited
        const isBeingEdited = editingMessage && editingMessage._id === message._id;
        const senderName = message.agent_name || null;
        const sentByName = message.sent_by_name || null;
        const shouldShowSenderDetails = (isSent && (senderName || sentByName)) || (!!sentByName);
        
        // Check if message has notes
        const hasNotes = message.notes && message.notes.length > 0;
        
        return (
            <div key={message._id} className={messageClass} data-message-status={message.status} data-message-id={message._id}>
                {shouldShowEdnVisibilityBadge && (
                    <span className={`edn-message-visibility-badge ${ednMessageVisibility}`}>
                        {ednMessageVisibility === 'admin'
                            ? t('messageWindow.visibilityAdminShort', 'Admin')
                            : t('messageWindow.visibilityAllShort', 'All')}
                    </span>
                )}
                {isTeamOutside && (
                    <div className="message-origin-label">
                        <FaExternalLinkAlt aria-hidden="true" />
                        <span>
                            {t('messageWindow.origin.outside', 'Sent outside Unanimity · {{source}}', { source: outsideSourceLabel(t, message.outside_source) })}
                        </span>
                        {outsideAuthor(message) && (
                            <span className="message-origin-author">{outsideAuthor(message)}</span>
                        )}
                    </div>
                )}
                {shouldShowSenderDetails && (
                    <div className="message-agent-name">
                        {senderName && (
                            <span className="message-agent-name-primary">
                                {senderName}
                            </span>
                        )}
                        {sentByName && (
                            <span className={`message-sent-by-name ${senderName ? 'with-primary' : ''}`}>
                                {t('messageWindow.sentByLabel', 'Sent by')} {sentByName}
                            </span>
                        )}
                    </div>
                )}
                {message.status === 'automated' && (
                    <span className="message-status-badge automated">{t('messageWindow.automated')}</span>
                )}
                
                {isBeingEdited ? (
                    // Edit mode UI
                    <div className="message-edit-container">
                        <textarea
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                            className="message-edit-textarea"
                            rows={4}
                            placeholder={t('messageWindow.editMessage')}
                        />
                        <div className="edit-actions">
                            <button 
                                className="edit-action-button save" 
                                onClick={handleSaveModifiedMessage}
                            >
                                <FaCheck /> {t('messageWindow.save')}
                            </button>
                            <button 
                                className="edit-action-button cancel" 
                                onClick={handleCancelModification}
                            >
                                <FaTimes /> {t('messageWindow.cancel')}
                            </button>
                        </div>
                    </div>
                ) : (
                    // Normal message display
                    <>
                        <div className="message-content">
                            {renderMessageContent(message)}
                        </div>
                        {renderQuotedHistory(message)}
                    </>
                )}

                {renderMessageAttachments(message._id)}
                
                <div className="message-meta">
                    <span className="message-status">
                        {message.status 
                            ? t(`messageWindow.statusOptions.${message.status}`)
                            : t('messageWindow.statusOptions.received')}
                    </span>
                    <div className="message-timestamps">
                        {message.sent_at && (
                            <span className="message-timestamp sent-at">
                                {t('messageWindow.sentAt')}: {formatDate(message.sent_at)}
                                {message.sent_by_name && ` (${message.sent_by_name})`}
                            </span>
                        )}
                        <span className="message-timestamp created-at">
                            {message.sent_at ? `${t('messageWindow.createdAt')}: ` : ''}{formatDate(message.created_at)}
                        </span>
                    </div>
                    
                    <div className="message-actions">
                        {/* Add note button and indicator */}
                        {user && user.role !== 'experience' && (
                            <div className="message-note-actions">
                                <button 
                                    className={`note-button ${hasNotes ? 'has-notes' : ''}`}
                                    onClick={() => openNoteModal(message)}
                                    title={t('messageWindow.addNote')}
                                >
                                    <FaStickyNote />
                                    {hasNotes && <span className="note-count">{message.notes.length}</span>}
                                </button>
                            </div>
                        )}

                        {/* Link to the AI trace that produced this message (messages.run_id) */}
                        {message.run_id && (
                            <div className="message-run-actions">
                                <button
                                    type="button"
                                    className="run-link-button"
                                    onClick={() => openAiRun(message.run_id, message.span_id)}
                                    title={t('messageWindow.viewAiRunTitle', 'Voir la trace IA qui a produit ce message')}
                                >
                                    <FaProjectDiagram />
                                    <span className="feedback-text">{t('messageWindow.viewAiRun', 'Run IA')}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                
                {message.status === 'draft' && isEditable && !isBeingEdited && (
                    <>
                        {message.metadata?.send_policy?.reason === 'send_failed' && (
                            <div className="draft-send-failed" role="status">
                                {t('messageWindow.draftSendFailed', 'Automatic sending failed: this reply was not delivered. Check it, then send it.')}
                            </div>
                        )}
                        {draftPendingFiles[message._id] && draftPendingFiles[message._id].length > 0 && (
                            <div className="draft-pending-files-container">
                                {draftPendingFiles[message._id].map((file, index) => (
                                    <div key={index} className="pending-file-item">
                                        <span className="pending-file-icon">
                                            {getFileIcon(file.mime_type)}
                                        </span>
                                        <span className="pending-file-name">{file.name}</span>
                                        <button
                                            type="button"
                                            className="pending-file-remove"
                                            onClick={() => handleRemoveDraftPendingFile(message._id, index)}
                                            title={t('messageWindow.removeFile', 'Remove file')}
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        {(() => {
                            const held = Array.isArray(message.held_carrier_contacts) ? message.held_carrier_contacts : [];
                            const toSend = held.filter((contact) => !contact.failed);
                            const failed = held.filter((contact) => contact.failed);
                            if (held.length === 0) return null;
                            const names = (items) => items.map((contact) => contact.carrier_name || '?').join(', ');
                            return (
                                <div className="draft-carrier-contacts">
                                    {toSend.length > 0 && (
                                        <label title={t('messageWindow.draftCarrierContactsHint', 'The AI prepared this contact in the same run as the reply. Untick to keep it for a separate validation in the carrier panel.')}>
                                            <input
                                                type="checkbox"
                                                checked={!skipDraftCarrierContacts[message._id]}
                                                disabled={sendingDraftMessageId === message._id}
                                                onChange={(event) => setSkipDraftCarrierContacts((prev) => ({ ...prev, [message._id]: !event.target.checked }))}
                                            />
                                            {t('messageWindow.draftCarrierContacts', 'Also send the carrier contact prepared with this reply: {{carriers}}', { carriers: names(toSend) })}
                                        </label>
                                    )}
                                    {failed.length > 0 && (
                                        <span className="draft-carrier-contacts-failed">
                                            {t('messageWindow.draftCarrierContactsFailed', 'Carrier contact failed, not sent with this reply — fix it in the carrier panel: {{carriers}}', { carriers: names(failed) })}
                                        </span>
                                    )}
                                </div>
                            );
                        })()}
                        <div className="draft-actions">
                            <button 
                                className="draft-message-button send" 
                                data-tour="draft-approve"
                                onClick={() => handleSendDraftMessage(message._id)}
                                disabled={sendingDraftMessageId === message._id}
                            >
                                {sendingDraftMessageId === message._id ? (
                                    <>
                                        <FaCircleNotch className="spinner" /> {t('messageWindow.sending')}
                                    </>
                                ) : (
                                    <>
                                        <FaPaperPlane /> {t('messageWindow.send')}
                                    </>
                                )}
                            </button>
                            <button 
                                className="draft-message-button attach" 
                                onClick={() => handleDraftFileAttachClick(message._id)}
                                disabled={sendingDraftMessageId === message._id}
                            >
                                <FaPaperclip /> {t('messageWindow.attach', 'Attach')}
                            </button>
                            <button className="draft-message-button delete" onClick={() => handleDeleteDraftMessage(message._id)}>
                                <FaTimes /> {t('messageWindow.delete')}
                            </button>
                            <button className="draft-message-button modify" onClick={() => handleModifyDraftMessage(message)}>
                                <FaEdit /> {t('messageWindow.modify')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const handleValidateCarrierMessage = async (conversationId, approved) => {
        setValidatingMessage(true);
        try {
            const token = localStorage.getItem('access_token');
            const validation = await axios.post('/api/logistics/validate-carrier-message', 
                {
                    conversation_id: conversationId,
                    approved: approved
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            // Form / API carriers (FedEx, DHL, GLS) answer the outcome in the
            // body: a refused or failed send is {success: false, error}. It
            // used to be ignored — the buttons re-enabled and nothing said why.
            if (validation?.data?.success === false) {
                await fetchCarriersConversation();
                setOverlayState({
                    show: true,
                    message: t(
                        'messageWindow.carrierContactFailed',
                        'Carrier contact failed: {{error}}',
                        { error: validation.data.error || t('messageWindow.validationError') }
                    ),
                    success: false
                });
                return;
            }

            // Approval queues an async portal task. Poll until the message
            // leaves "processing" so a fast failure surfaces instead of
            // leaving the UI stuck on "Envoi en cours".
            let latestConversation = null;
            for (let attempt = 0; attempt < 8; attempt += 1) {
                await new Promise((resolve) => setTimeout(resolve, attempt === 0 ? 800 : 1500));
                const response = await axios.get(
                    `/api/tickets/carriers_conversation/${ticketId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                latestConversation = response.data;
                setCarriersConversation(latestConversation);
                const stillProcessing =
                    latestConversation?.status === 'processing' ||
                    (latestConversation?.messages || []).some((msg) => msg.status === 'processing');
                if (!stillProcessing) {
                    break;
                }
            }

            if (approved && latestConversation?.error) {
                setOverlayState({
                    show: true,
                    message: t(
                        'messageWindow.carrierContactFailed',
                        'Carrier contact failed: {{error}}',
                        { error: latestConversation.error }
                    ),
                    success: false
                });
            } else {
                setOverlayState({ 
                    show: true, 
                    message: approved ? t('messageWindow.messageApproved') : t('messageWindow.messageRejected'), 
                    success: true 
                });
            }
        } catch (error) {
            // A 4xx from the backend carries the operator's instruction
            // (e.g. no recipient address configured for the carrier).
            setOverlayState({ 
                show: true, 
                message: error.response?.data?.detail || t('messageWindow.validationError'), 
                success: false 
            });
        } finally {
            setValidatingMessage(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 4000);
        }
    };

    const getFileIcon = (mimeType) => {
        if (mimeType.includes('pdf')) {
            return <FaFilePdf />;
        } else if (mimeType.includes('image')) {
            return <FaFileImage />;
        } else if (mimeType.includes('zip') || mimeType.includes('rar')) {
            return <FaFileArchive />;
        } else if (mimeType.includes('code') || mimeType.includes('json')) {
            return <FaFileCode />;
        }
        return <FaFileAlt />;
    };

    const getFileClass = (mimeType) => {
        if (mimeType.includes('pdf')) {
            return 'pdf';
        } else if (mimeType.includes('image')) {
            return 'image';
        }
        return 'document';
    };

    // What the AI agent read in a file (read_ticket_file): title, description,
    // date. Shown on the attachment chips, the attachments list and the viewer.
    const readingDate = (reading) => (reading?.read_at && parseUtc(reading.read_at) ? formatDateTime(reading.read_at) : '');

    const attachmentTooltip = (file) => {
        if (isDecorativeFile(file)) {
            return [file?.name, t('messageWindow.decorativeFileHint', 'Signature, logo or icon of an e-mail: never read by the AI nor sent to a carrier.'),
                file?.metadata?.decorative_reason].filter(Boolean).join('\n\n');
        }
        if (!file?.reading) return file?.name || '';
        return [file.reading.title, file.reading.description, file.name].filter(Boolean).join('\n\n');
    };

    // Add this new component for the metadata tooltip
    const FileMetadataTooltip = ({ metadata }) => {
        if (!metadata || Object.keys(metadata).length === 0) return null;
        
        return (
            <div className="file-metadata-content">
                {Object.entries(metadata).map(([key, value]) => (
                    value && (
                        <div key={key} className={`metadata-item metadata-${key}`}>
                            <strong>{t(`messageWindow.metadata.${key}`, key)}:</strong> {value}
                        </div>
                    )
                ))}
            </div>
        );
    };

    // Add file processing function
    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                // Remove the data:image/jpeg;base64, prefix
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = error => reject(error);
        });
    };

    // Handle file selection - store files in state to be sent with message
    const handleFileSelection = async (event) => {
        const files = Array.from(event.target.files);
        
        if (files.length === 0) return;
        
        // Validate file count (max 10 files total including already pending)
        if (pendingFiles.length + files.length > 10) {
            setOverlayState({
                show: true,
                message: t('messageWindow.maxFilesError'),
                success: false
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
            return;
        }

        // Validate total file size (max 50MB total)
        const existingSize = pendingFiles.reduce((sum, f) => sum + f.size, 0);
        const newSize = files.reduce((sum, file) => sum + file.size, 0);
        const maxSize = 50 * 1024 * 1024; // 50MB
        
        if (existingSize + newSize > maxSize) {
            setOverlayState({
                show: true,
                message: t('messageWindow.fileSizeError'),
                success: false
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
            return;
        }

        setUploadingFiles(true);
        
        try {
            const processedFiles = [];

            // Process all files to base64
            for (const file of files) {
                const base64Content = await convertFileToBase64(file);
                processedFiles.push({
                    name: file.name,
                    mime_type: file.type,
                    content: base64Content,
                    size: file.size
                });
            }

            // Add to pending files instead of uploading
            setPendingFiles(prev => [...prev, ...processedFiles]);
            
            // Clear file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            setOverlayState({
                show: true,
                message: t('messageWindow.fileProcessError', 'Error processing files'),
                success: false
            });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
        } finally {
            setUploadingFiles(false);
        }
    };

    // Function to remove a pending file
    const handleRemovePendingFile = (index) => {
        setPendingFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleDraftFileAttachClick = (messageId) => {
        setActiveDraftForFileUpload(messageId);
        setTimeout(() => draftFileInputRef.current?.click(), 0);
    };

    const handleDraftFileSelection = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0 || !activeDraftForFileUpload) return;

        const messageId = activeDraftForFileUpload;
        const existing = draftPendingFiles[messageId] || [];

        if (existing.length + files.length > 10) {
            setOverlayState({ show: true, message: t('messageWindow.maxFilesError'), success: false });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
            if (draftFileInputRef.current) draftFileInputRef.current.value = '';
            return;
        }

        const existingSize = existing.reduce((sum, f) => sum + f.size, 0);
        const newSize = files.reduce((sum, file) => sum + file.size, 0);
        if (existingSize + newSize > 50 * 1024 * 1024) {
            setOverlayState({ show: true, message: t('messageWindow.fileSizeError'), success: false });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
            if (draftFileInputRef.current) draftFileInputRef.current.value = '';
            return;
        }

        try {
            const processedFiles = [];
            for (const file of files) {
                const base64Content = await convertFileToBase64(file);
                processedFiles.push({
                    name: file.name,
                    mime_type: file.type,
                    content: base64Content,
                    size: file.size
                });
            }
            setDraftPendingFiles(prev => ({
                ...prev,
                [messageId]: [...(prev[messageId] || []), ...processedFiles]
            }));
        } catch (error) {
            setOverlayState({ show: true, message: t('messageWindow.fileProcessError', 'Error processing files'), success: false });
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
        } finally {
            if (draftFileInputRef.current) draftFileInputRef.current.value = '';
            setActiveDraftForFileUpload(null);
        }
    };

    const handleRemoveDraftPendingFile = (messageId, index) => {
        setDraftPendingFiles(prev => {
            const updated = { ...prev };
            updated[messageId] = (updated[messageId] || []).filter((_, i) => i !== index);
            if (updated[messageId].length === 0) delete updated[messageId];
            return updated;
        });
    };

    // Add API function for adding notes
    const handleAddNote = async () => {
        if (!noteContent.trim() || !selectedMessageForNote) return;

        setAddingNote(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.post('/api/messages/add_note', {
                message_id: selectedMessageForNote._id,
                note: noteContent.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Refresh messages to show the new note
            await fetchMessages();
            
            // Reset note form
            setNoteContent('');
            setSelectedMessageForNote(null);
            setShowNoteModal(false);
            
            setOverlayState({
                show: true,
                message: t('messageWindow.noteAddedSuccess'),
                success: true
            });
        } catch (error) {
            setOverlayState({
                show: true,
                message: t('messageWindow.noteAddError'),
                success: false
            });
        } finally {
            setAddingNote(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 2000);
        }
    };

    // Function to open note modal
    const openNoteModal = (message) => {
        setSelectedMessageForNote(message);
        setNoteContent('');
        setShowNoteModal(true);
    };

    // Function to close note modal
    const closeNoteModal = () => {
        setShowNoteModal(false);
        setNoteContent('');
        setSelectedMessageForNote(null);
    };

    // Function to format note date
    const formatNoteDate = (dateString) => {
        const options = { 
            month: 'short', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit',
            timeZone: 'Europe/Paris'
        };
        // Naive backend strings are UTC (shared parser, KI-110).
        const parsed = parseUtc(dateString);
        return parsed ? parsed.toLocaleDateString('fr-FR', options) : '';
    };

    // Add function to handle AI handoff
    const handleAIHandoff = async () => {
        if (!handoffInstructions.trim() || !user) return;

        setHandingOffToAI(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.post(`/api/tickets/${ticketId}/agent-handoff`, {
                instructions: handoffInstructions.trim(),
                user_id: user._id
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const successMessage = t('messageWindow.handoffSuccess', {
                defaultValue: 'Ticket successfully handed off to AI',
                automation: response.data.automation_enabled ? 
                    t('messageWindow.messageSent', 'Message sent') : 
                    t('messageWindow.savedAsDraft', 'Saved as draft')
            });
            
            redirectWithNotification(successMessage, true);
        } catch (error) {
            const errorMessage = error.response?.data?.detail || t('messageWindow.handoffError', 'Failed to hand off ticket to AI');
            redirectWithNotification(errorMessage, false);
        } finally {
            setHandingOffToAI(false);
        }
    };

    // Add function to open handoff modal
    const openHandoffModal = () => {
        setHandoffInstructions('');
        setShowHandoffModal(true);
    };

    // Add function to close handoff modal
    const closeHandoffModal = () => {
        setShowHandoffModal(false);
        setHandoffInstructions('');
    };

    // Add function to handle Slack message sending
    const handleSendSlackMessage = async () => {
        if (!slackMessage.trim() || !ticketId) return;

        setSendingSlackMessage(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.post('/api/send-slack-message', {
                ticket_id: ticketId,
                message: slackMessage.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Reset form and close modal
            setSlackMessage('');
            setShowSlackModal(false);
            
            setOverlayState({
                show: true,
                message: t('messageWindow.slackMessageSentSuccess', 'Message sent to support team successfully'),
                success: true
            });
        } catch (error) {
            setOverlayState({
                show: true,
                message: error.response?.data?.detail || t('messageWindow.slackMessageSendError', 'Failed to send message to support team'),
                success: false
            });
        } finally {
            setSendingSlackMessage(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
        }
    };

    // Add function to open Slack modal
    const openSlackModal = () => {
        setSlackMessage('');
        setShowSlackModal(true);
    };

    // Add function to close Slack modal
    const closeSlackModal = () => {
        setShowSlackModal(false);
        setSlackMessage('');
    };

    // Functions for bug report
    const openBugReportModal = () => {
        setBugDescription('');
        setShowBugReportModal(true);
    };

    const closeBugReportModal = () => {
        if (sendingBugReport) return;
        setShowBugReportModal(false);
        setBugDescription('');
    };

    const handleSendBugReport = async () => {
        if (!bugDescription.trim() || !ticketId) return;

        setSendingBugReport(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.post(`/api/tickets/${ticketId}/report-bug`, {
                description: bugDescription.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setBugDescription('');
            setShowBugReportModal(false);

            setOverlayState({
                show: true,
                message: t('messageWindow.bugReportSentSuccess', 'Bug report sent to the support team'),
                success: true
            });
        } catch (error) {
            const detail = error.response?.data?.detail;
            const message = typeof detail === 'object' && detail !== null
                ? (detail.message || t('messageWindow.bugReportSendError', 'Failed to send the bug report'))
                : (detail || t('messageWindow.bugReportSendError', 'Failed to send the bug report'));
            setOverlayState({
                show: true,
                message,
                success: false
            });
        } finally {
            setSendingBugReport(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
        }
    };

    // Functions for email forwarding
    const parseEmailList = (input) => {
        return input
            .split(/[,;\s]+/)
            .map((email) => email.trim())
            .filter((email) => email.length > 0);
    };

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const forwardToEmails = parseEmailList(forwardToInput);
    const forwardCcEmails = parseEmailList(forwardCcInput);
    const forwardEmailsValid =
        forwardToEmails.length > 0 &&
        forwardToEmails.every(isValidEmail) &&
        forwardCcEmails.every(isValidEmail);

    const openForwardModal = () => {
        setForwardToInput('');
        setForwardCcInput('');
        setForwardComment('');
        setShowForwardModal(true);
    };

    const closeForwardModal = () => {
        if (forwardingEmail) return;
        setShowForwardModal(false);
        setForwardToInput('');
        setForwardCcInput('');
        setForwardComment('');
    };

    const handleForwardEmail = async () => {
        if (!forwardEmailsValid || !ticketId || forwardingEmail) return;

        setForwardingEmail(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.post('/api/messages/forward', {
                ticket_id: ticketId,
                forward_to_emails: forwardToEmails,
                cc_emails: forwardCcEmails.length > 0 ? forwardCcEmails : null,
                comment: forwardComment.trim() || null
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setShowForwardModal(false);
            setForwardToInput('');
            setForwardCcInput('');
            setForwardComment('');

            setOverlayState({
                show: true,
                message: t('messageWindow.forwardEmailSuccess', 'Email forwarded successfully'),
                success: true
            });

            fetchMessages();
        } catch (error) {
            setOverlayState({
                show: true,
                message: error.response?.data?.detail || t('messageWindow.forwardEmailError', 'Failed to forward the email'),
                success: false
            });
        } finally {
            setForwardingEmail(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
        }
    };

    // Functions for restart AI processing
    const openRestartAIModal = () => {
        setSelectedAssistantId('');
        setShowRestartAIModal(true);
        if (availableAssistants.length === 0) {
            fetchAvailableAssistants();
        }
    };

    const closeRestartAIModal = () => {
        setShowRestartAIModal(false);
        setSelectedAssistantId('');
    };

    const handleRestartAIProcessing = async () => {
        if (!selectedAssistantId || !user || restartingAI) return;

        setRestartingAI(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.post(`/api/tickets/${ticketId}/restart-ai-processing`, {
                assistant_id: selectedAssistantId,
                user_id: user._id
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            closeRestartAIModal();
            
            setOverlayState({
                show: true,
                message: t('messageWindow.restartAISuccess', 'AI processing restarted successfully. New response generating in background.'),
                success: true
            });

            // Refresh messages after a short delay to show the new AI response
            setTimeout(() => {
                fetchMessages();
                fetchTicketContext();
            }, 2000);
        } catch (error) {
            const errorMessage = error.response?.data?.detail || t('messageWindow.restartAIError', 'Failed to restart AI processing');
            setOverlayState({
                show: true,
                message: errorMessage,
                success: false
            });
        } finally {
            setRestartingAI(false);
            setTimeout(() => setOverlayState({ show: false, message: '', success: false }), 3000);
        }
    };

    const displayedMessages = messages.filter((message) => {
        const isDraftLike = message.status === 'draft' || message.status === 'superseded';
        if (hideDraftMessages && isDraftLike) {
            return false;
        }

        const messageText = normalizeMessageContent(message.content).trim();
        const hasPersistedFiles = (messageFiles[message._id] || []).length > 0;
        const hasPendingDraftFiles = (draftPendingFiles[message._id] || []).length > 0;

        return !isDraftLike || messageText || hasPersistedFiles || hasPendingDraftFiles;
    });

    return (
        <div className="app-container">
            <div className="message-window-layout">
                <div className="ticket-header">
                    <div className="ticket-title-row">
                        <div className="ticket-title-container">
                            <div className="ticket-title-block">
                                <h2 title={ticket.subject}>{ticket.subject}</h2>
                                {(ticket.metadata || {}).original_subject && ticket.metadata.original_subject !== ticket.subject && (
                                    <div className="ticket-original-subject" title={ticket.metadata.original_subject}>
                                        {t('messageWindow.originalSubject', "Objet d'origine")}: {ticket.metadata.original_subject}
                                    </div>
                                )}
                                {relatedTickets.previous && (
                                    <div className="ticket-related">
                                        <FaHistory aria-hidden="true" />
                                        <span>{t('messageWindow.continuesTicket', 'Suite du ticket')}</span>
                                        <Link to={`/tickets/${relatedTickets.previous._id}/messages`} title={relatedTickets.previous.subject}>
                                            {relatedTickets.previous.subject || t('messageWindow.untitledTicket', 'Ticket sans objet')}
                                        </Link>
                                        <span className="ticket-related-meta">
                                            {'· '}{getStatusLabel(t, relatedTickets.previous.status)}
                                            {relatedTickets.previous.created_at ? ` · ${formatDateTime(relatedTickets.previous.created_at, { hour: undefined, minute: undefined })}` : ''}
                                        </span>
                                    </div>
                                )}
                                {relatedTickets.next.length > 0 && (
                                    <div className="ticket-related">
                                        <FaArrowRight aria-hidden="true" />
                                        <span>{t('messageWindow.continuedBy', 'Suivi par')}</span>
                                        {relatedTickets.next.map((related) => (
                                            <Link key={related._id} to={`/tickets/${related._id}/messages`} title={related.subject}>
                                                {related.subject || t('messageWindow.untitledTicket', 'Ticket sans objet')}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                                <TicketAgentNotices
                                    ticket={ticket}
                                    onTicketChange={(changes) => setTicket(prev => (prev ? { ...prev, ...changes } : prev))}
                                />
                            </div>
                        </div>
                        <div className="header-actions">
                            {isEdnCustomUser ? (
                                <div className="edn-status-dropdown-container" ref={ednStatusDropdownRef}>
                                    <button
                                        type="button"
                                        className={`status-badge edn-status-trigger ${ticket.status}`}
                                        data-tour="ticket-status"
                                        onClick={handleEdnStatusDropdownToggle}
                                        disabled={updatingEdnStatus}
                                        title={t('messageWindow.changeTicketStatus', 'Change ticket status')}
                                    >
                                        <span>{ticketStatusLabel}</span>
                                        {updatingEdnStatus ? (
                                            <FaCircleNotch className="spinner" />
                                        ) : (
                                            <FaChevronDown className="edn-status-chevron" />
                                        )}
                                    </button>
                                    {showEdnStatusDropdown && (
                                        <div className="edn-status-dropdown">
                                            <div className="edn-status-dropdown-header">
                                                {t('messageWindow.selectStatus', 'Select status')}
                                            </div>
                                            {ednStatusesLoading ? (
                                                <div className="edn-status-dropdown-loading">
                                                    <FaCircleNotch className="spinner" />
                                                    {t('messageWindow.loadingStatuses', 'Loading statuses...')}
                                                </div>
                                            ) : (
                                                <div className="edn-status-dropdown-list">
                                                    {ednStatusOptions.map((statusOption) => (
                                                        <button
                                                            key={statusOption.value}
                                                            type="button"
                                                            className={`edn-status-dropdown-item ${ticket.status === statusOption.value ? 'active' : ''}`}
                                                            onClick={() => handleUpdateEdnStatus(statusOption.value)}
                                                            disabled={updatingEdnStatus || ticket.status === statusOption.value}
                                                        >
                                                            <span>{statusOption.name}</span>
                                                            {ticket.status === statusOption.value && (
                                                                <FaCheck className="edn-status-check" />
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <span className={`status-badge ${ticket.status}`} data-tour="ticket-status">
                                    {ticketStatusLabel}
                                </span>
                            )}
                            {ticketFiles.length > 0 && (
                                <div className="attachments-dropdown-container" ref={attachmentsDropdownRef}>
                                    <button 
                                        className="attachments-dropdown-trigger"
                                        onClick={() => setShowAttachmentsDropdown(!showAttachmentsDropdown)}
                                        title={t('messageWindow.attachments')}
                                    >
                                        <FaFile />
                                        <span className="attachments-count">{documentTicketFiles.length}</span>
                                    </button>
                                    {showAttachmentsDropdown && (
                                        <div className="attachments-dropdown">
                                            <div className="attachments-dropdown-header">
                                                {t('messageWindow.attachments')} ({documentTicketFiles.length})
                                            </div>
                                            <div className="attachments-dropdown-list">
                                                {[...documentTicketFiles, ...(showDecorativeFiles ? decorativeTicketFiles : [])].map((file, index) => (
                                                    <button
                                                        key={index}
                                                        className={`attachments-dropdown-item ${getFileClass(file.mime_type)}`}
                                                        onClick={() => {
                                                            handleFileClick(file);
                                                            setShowAttachmentsDropdown(false);
                                                        }}
                                                        title={attachmentTooltip(file)}
                                                    >
                                                        <span className="attachment-icon">{getFileIcon(file.mime_type)}</span>
                                                        <span className="attachment-text">
                                                            <span className="attachment-name">{file.reading?.title || file.name}</span>
                                                            {isDecorativeFile(file) ? (
                                                                <span className="attachment-reading attachment-decorative">
                                                                    {t('messageWindow.decorativeFile', 'Signature image')}
                                                                </span>
                                                            ) : file.reading && (
                                                                <span className="attachment-reading">
                                                                    <FaRobot aria-hidden="true" />
                                                                    {file.name} · {t('messageWindow.readByAi', "Lu par l'IA")}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </button>
                                                ))}
                                                {decorativeTicketFiles.length > 0 && (
                                                    <button
                                                        type="button"
                                                        className="attachments-dropdown-item attachments-decorative-toggle"
                                                        onClick={() => setShowDecorativeFiles((value) => !value)}
                                                        title={t('messageWindow.decorativeFileHint', 'Signature, logo or icon of an e-mail: never read by the AI nor sent to a carrier.')}
                                                        aria-expanded={showDecorativeFiles}
                                                    >
                                                        <span className="attachment-text">
                                                            <span className="attachment-reading">
                                                                {t('messageWindow.decorativeFiles', 'Signature images ({{count}})', { count: decorativeTicketFiles.length })}
                                                            </span>
                                                        </span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* Primary action: resolve the ticket */}
                            {isEditable && (
                                <button
                                    type="button"
                                    className="header-btn header-btn--primary"
                                    data-tour="resolve-ticket"
                                    onClick={handleResolveTicket}
                                    title={t('messageWindow.resolveTicket')}
                                >
                                    <FaCheck />
                                    <span>{t('messageWindow.resolveTicket')}</span>
                                </button>
                            )}

                            {/* Add assignment dropdown */}
                            {user && user.role !== 'experience' && isEditable && (
                                <div className="assignment-dropdown-container" ref={assignmentDropdownRef}>
                                    <button 
                                        className="assignment-trigger"
                                        onClick={() => setShowAssignmentDropdown(!showAssignmentDropdown)}
                                        disabled={assigningTicket}
                                        title={t('messageWindow.assignToEmployee')}
                                    >
                                        <FaUserPlus />
                                    </button>
                                    {showAssignmentDropdown && (
                                        <div className="assignment-dropdown">
                                            <div className="assignment-dropdown-header">
                                                {t('messageWindow.assignToEmployee')}
                                            </div>
                                            {employees.length > 0 ? (
                                                <div className="assignment-dropdown-list">
                                                    {employees.map((employee) => (
                                                        <button
                                                            key={employee._id}
                                                            className="assignment-dropdown-item"
                                                            onClick={() => handleAssignTicket(employee._id)}
                                                            disabled={assigningTicket}
                                                        >
                                                            {employee.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="assignment-dropdown-empty">
                                                    {t('messageWindow.noEmployeesAvailable')}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* AI handoff stays visible: it is a frequent action */}
                            {user && user.role !== 'experience' && isEditable && (
                                <button
                                    className="ai-handoff-button"
                                    onClick={openHandoffModal}
                                    disabled={handingOffToAI}
                                    title={t('messageWindow.handoffToAI')}
                                >
                                    <FaRobot />
                                </button>
                            )}

                            {/* Everything else lives in one labeled overflow menu */}
                            <div className="action-menu-container" ref={actionMenuRef}>
                                <button
                                    className="action-menu-trigger"
                                    data-tour="ticket-actions"
                                    onClick={() => setShowActionMenu(!showActionMenu)}
                                    title={t('messageWindow.moreActions', "Plus d'actions")}
                                >
                                    <FaEllipsisV />
                                </button>
                                {showActionMenu && (
                                    <div className="action-menu">
                                        {user && user.role !== 'experience' && isEditable && (
                                            <button
                                                onClick={() => { setShowActionMenu(false); openRestartAIModal(); }}
                                                disabled={restartingAI}
                                            >
                                                <FaSync /> {t('messageWindow.restartAIProcessing', 'Restart AI Processing')}
                                            </button>
                                        )}
                                        {user && user.role !== 'experience' && (
                                            <>
                                                <button
                                                    onClick={() => { setShowActionMenu(false); handleGetTicketSummary(); }}
                                                    disabled={loadingSummary}
                                                >
                                                    {loadingSummary ? <FaCircleNotch className="spinner" /> : <FaClipboardList />}
                                                    {t('messageWindow.ticketSummary', 'Ticket Summary')}
                                                </button>
                                                <button
                                                    onClick={() => { setShowActionMenu(false); openForwardModal(); }}
                                                    disabled={forwardingEmail}
                                                >
                                                    <FaShareSquare /> {t('messageWindow.forwardEmail', 'Forward by email')}
                                                </button>
                                                <button
                                                    onClick={() => { setShowActionMenu(false); openSlackModal(); }}
                                                    disabled={sendingSlackMessage}
                                                >
                                                    <FaSlack /> {t('messageWindow.sendSlackMessage')}
                                                </button>
                                                <button
                                                    onClick={() => { setShowActionMenu(false); openBugReportModal(); }}
                                                    disabled={sendingBugReport}
                                                >
                                                    <FaBug /> {t('messageWindow.reportBug', 'Report a bug')}
                                                </button>
                                            </>
                                        )}
                                        {isEdnCustomUser && (
                                            <button
                                                onClick={() => { setShowActionMenu(false); handleGetStatusHistory(); }}
                                                disabled={loadingStatusHistory}
                                            >
                                                {loadingStatusHistory ? <FaCircleNotch className="spinner" /> : <FaClock />}
                                                {t('messageWindow.statusHistory', 'Status history')}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setHideDraftMessages((prev) => !prev)}
                                            aria-pressed={hideDraftMessages}
                                        >
                                            <FaFilter />
                                            {hideDraftMessages
                                                ? t('messageWindow.showDrafts', 'Show drafts')
                                                : t('messageWindow.hideDrafts', 'Hide drafts')}
                                        </button>
                                        <div className="action-menu-divider" />
                                        <div className="action-menu-section-label">
                                            <FaLanguage /> {t('messageWindow.translationSection', 'Traduction')}
                                        </div>
                                        <button
                                            className={targetLanguage === 'original' ? 'active-option' : ''}
                                            onClick={() => { setShowActionMenu(false); handleLanguageChange('original'); }}
                                        >
                                            {t('messageWindow.original')}
                                        </button>
                                        <button
                                            className={targetLanguage === 'en' ? 'active-option' : ''}
                                            data-tour="translate-en"
                                            onClick={() => { setShowActionMenu(false); handleLanguageChange('en'); }}
                                        >
                                            English
                                        </button>
                                        <button
                                            className={targetLanguage === 'fr' ? 'active-option' : ''}
                                            onClick={() => { setShowActionMenu(false); handleLanguageChange('fr'); }}
                                        >
                                            Français
                                        </button>
                                        <button
                                            className={targetLanguage === 'es' ? 'active-option' : ''}
                                            onClick={() => { setShowActionMenu(false); handleLanguageChange('es'); }}
                                        >
                                            Español
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="ticket-meta">
                        <span className="ticket-employee" title={t('messageWindow.employee')}>
                            <FaUser />
                            <span>
                                {employee && employee !== 'N/A' && String(employee).toLowerCase() !== 'not assigned'
                                    ? employee
                                    : t('messageWindow.notAssigned', 'Non assigné')}
                            </span>
                        </span>
                        {ticket.agent_wakeup?.state === 'scheduled' && ticket.agent_wakeup?.due_at && (
                            // The agent's next wake-up on this case (agent_wakeup_service).
                            <div className="ticket-indicator wakeup"
                                title={t('messageWindow.wakeupReason', 'Raison : {{reason}}', { reason: ticket.agent_wakeup.reason || '' })}>
                                <FaClock />
                                <span>{t('messageWindow.wakeupAt', 'Réveil IA {{date}}', { date: formatDateTime(ticket.agent_wakeup.due_at) })}</span>
                            </div>
                        )}
                        {ticket.redirected_to && (
                            <div className="ticket-redirect-indicator" title={t('messageWindow.redirectedTo', 'Forwarded to')}>
                                <FaShareSquare />
                                <span>{t('messageWindow.forwardedTo', 'Forwarded to')} {ticket.redirected_to}</span>
                            </div>
                        )}
                        {ticket.channel_name && (
                            <div className="ticket-indicator channel" title={t('messageWindow.channel', 'Channel')}>
                                {(() => {
                                    const ChannelIcon = channelIcons[ticket.channel_name] || FaEnvelope;
                                    return <ChannelIcon />;
                                })()}
                                <span>{ticket.channel_name}</span>
                            </div>
                        )}
                        {brand?.name && (
                            <div className="ticket-indicator brand" title={t('messageWindow.brand', 'Brand')}>
                                <FaStore />
                                <span>{brand.name}</span>
                            </div>
                        )}
                        <div className="ticket-indicators">
                            {contextData && (
                                <>
                                    <button
                                        type="button"
                                        className="ticket-indicator tracking copyable"
                                        onClick={handleCopyTracking}
                                        title={t('messageWindow.copyTracking', 'Copier le numéro de suivi')}
                                        disabled={!contextData.shipping_info.master_tracking}
                                    >
                                        <FaBarcode />
                                        <span>{contextData.shipping_info.master_tracking || t('messageWindow.noTracking')}</span>
                                        {contextData.shipping_info.master_tracking && (
                                            trackingCopied
                                                ? <FaCheck className="copy-icon copy-check" />
                                                : <FaCopy className="copy-icon" />
                                        )}
                                    </button>
                                    {contextData.shipping_info.external_ticket_id && (
                                        <div className="ticket-indicator tracking" title={t('messageWindow.externalTicketId', 'External Ticket ID')}>
                                            <FaIdCard />
                                            <span>{contextData.shipping_info.external_ticket_id}</span>
                                        </div>
                                    )}
                                    <div className="topic-dropdown-container" ref={topicDropdownRef}>
                                        <button 
                                            className="ticket-indicator tracking topic-editable"
                                            onClick={handleTopicDropdownToggle}
                                            title={t('messageWindow.clickToChangeTopic', 'Click to change topic')}
                                            disabled={updatingTopic || !isEditable}
                                        >
                                            <FaTag />
                                            <span>{currentTopicLabel}</span>
                                            {isEditable && <FaChevronDown className="topic-chevron" />}
                                        </button>
                                        {showTopicDropdown && (
                                            <div className="topic-dropdown">
                                                <div className="topic-dropdown-header">
                                                    {t('messageWindow.selectTopic', 'Select Topic')}
                                                </div>
                                                {topicsLoading ? (
                                                    <div className="topic-dropdown-loading">
                                                        <FaCircleNotch className="spinner" />
                                                        {t('messageWindow.loadingTopics', 'Loading topics...')}
                                                    </div>
                                                ) : availableTopics.length > 0 ? (
                                                    <div className="topic-dropdown-list">
                                                        {availableTopics.map((topic) => {
                                                            const isActiveOption = isEdnCustomUser
                                                                ? Number(currentTopicId) === Number(topic.id)
                                                                : topic.kind === 'classification'
                                                                    ? ticket.topic_category === topic.value
                                                                    : currentTopicValue === topic.value;
                                                            return (
                                                                <button
                                                                    key={topic.id || topic.value}
                                                                    className={`topic-dropdown-item ${isActiveOption ? 'active' : ''} ${topic.type === 'custom' ? 'custom' : ''}`}
                                                                    onClick={() => handleUpdateTopic(topic)}
                                                                    disabled={updatingTopic || isActiveOption}
                                                                >
                                                                    <span className="topic-label">{topic.label}</span>
                                                                    {topic.type === 'custom' && (
                                                                        <span className="topic-badge custom">{t('messageWindow.customTopic', 'Custom')}</span>
                                                                    )}
                                                                    {topic.kind === 'classification' && (
                                                                        <span className="topic-badge classification">{t('messageWindow.classificationTopic', 'Topic')}</span>
                                                                    )}
                                                                    {isActiveOption && (
                                                                        <FaCheck className="topic-check" />
                                                                    )}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <div className="topic-dropdown-empty">
                                                        {t('messageWindow.noTopicsAvailable', 'No topics available')}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {contextData.client_info.editable ? (
                                        <div className="topic-dropdown-container" ref={clientDropdownRef}>
                                            <button
                                                className={`ticket-indicator client topic-editable ${contextData.client_info.name ? '' : 'client-missing'}`}
                                                onClick={handleClientDropdownToggle}
                                                title={t('messageWindow.clickToChangeClient', 'Click to set the ticket client')}
                                                disabled={updatingClient}
                                            >
                                                <FaUser />
                                                <span>{contextData.client_info.name || t('messageWindow.selectClientPrompt', 'No client · choose')}</span>
                                                <FaChevronDown className="topic-chevron" />
                                            </button>
                                            {showClientDropdown && (
                                                <div className="topic-dropdown">
                                                    <div className="topic-dropdown-header">
                                                        {t('messageWindow.selectClient', 'Ticket client')}
                                                    </div>
                                                    {clientOptionsLoading ? (
                                                        <div className="topic-dropdown-loading">
                                                            <FaCircleNotch className="spinner" />
                                                            {t('messageWindow.loadingClients', 'Loading clients...')}
                                                        </div>
                                                    ) : clientOptions.length > 0 ? (
                                                        <>
                                                            <input
                                                                type="text"
                                                                className="client-dropdown-search"
                                                                value={clientFilter}
                                                                onChange={(e) => setClientFilter(e.target.value)}
                                                                placeholder={t('messageWindow.searchClient', 'Search a client')}
                                                                autoFocus
                                                            />
                                                            <div className="topic-dropdown-list">
                                                                {clientOptions
                                                                    .filter((option) => option.name.toLowerCase().includes(clientFilter.trim().toLowerCase()))
                                                                    .map((option) => {
                                                                        const isActiveOption = option.name === contextData.client_info.name;
                                                                        return (
                                                                            <button
                                                                                key={option.id}
                                                                                className={`topic-dropdown-item ${isActiveOption ? 'active' : ''}`}
                                                                                onClick={() => handleSelectClient(option)}
                                                                                disabled={updatingClient || isActiveOption}
                                                                            >
                                                                                <span className="topic-label">{option.name}</span>
                                                                                {isActiveOption && <FaCheck className="topic-check" />}
                                                                            </button>
                                                                        );
                                                                    })}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="topic-dropdown-empty">
                                                            {t('messageWindow.noClientsConfigured', 'No client configured: add your clients on the Clients page.')}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="ticket-indicator client" title={t('messageWindow.clientName')}>
                                            <FaUser />
                                            <span>{contextData.client_info.name || t('messageWindow.noClient')}</span>
                                        </div>
                                    )}
                                    <div className="ticket-indicator carrier" title={t('messageWindow.carrierName')}>
                                        <FaTruck />
                                        <span>{contextData.shipping_info.carrier_name || t('messageWindow.noCarrier')}</span>
                                    </div>
                                    <div
                                        className="shipment-progress"
                                        title={`${t('messageWindow.shipped')} · ${t('messageWindow.delivered')}`}
                                    >
                                        <span className={`shipment-step ${contextData.shipping_info.is_shipped ? 'done' : ''}`}>
                                            <FaShippingFast />
                                            {t('messageWindow.shipped')}
                                        </span>
                                        <span className={`shipment-step ${contextData.shipping_info.is_delivered ? 'done' : ''}`}>
                                            <FaBox />
                                            {t('messageWindow.delivered')}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                
                <div className="message-window-content">
                    <div className="message-window-messages">
                            {displayedMessages.map((message) => (
                            <React.Fragment key={`fragment-${message._id}`}>
                                {renderMessage(message)}
                                {renderNoteCard(message)}
                            </React.Fragment>
                        ))}
                        {/* The resolution's justification, written by the AI that resolved the ticket */}
                        {!isEditable && ticket.summary && ticket.summary_meta?.by === 'ai' && (
                            <section className="resolution-note" aria-label={t('messageWindow.resolutionNote', 'Justificatif de résolution')}>
                                <header>
                                    <FaRobot aria-hidden="true" />
                                    <strong>{t('messageWindow.resolutionNote', 'Justificatif de résolution')}</strong>
                                    {ticket.summary_meta?.at && (
                                        <span>{t('messageWindow.resolvedByAiOn', "Résolu par l'IA le {{date}}", { date: formatDateTime(ticket.summary_meta.at) })}</span>
                                    )}
                                </header>
                                <p><LinkifiedText text={ticket.summary} /></p>
                            </section>
                        )}
                    </div>

                    {isEditable && (
                        <div className="input-container">
                            <div className="message-input-wrapper">
                                {/* Display pending files */}
                                {pendingFiles.length > 0 && (
                                    <div className="pending-files-container">
                                        {pendingFiles.map((file, index) => (
                                            <div key={index} className="pending-file-item">
                                                <span className="pending-file-icon">
                                                    {getFileIcon(file.mime_type)}
                                                </span>
                                                <span className="pending-file-name">{file.name}</span>
                                                <button
                                                    type="button"
                                                    className="pending-file-remove"
                                                    onClick={() => handleRemovePendingFile(index)}
                                                    title={t('messageWindow.removeFile', 'Remove file')}
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div 
                                    className="composer-field"
                                    data-tour="composer"
                                    onClick={handleComposerClick}
                                >
                                    <textarea
                                        ref={textareaRef}
                                        value={newMessage}
                                        onChange={handleTextareaChange}
                                        placeholder={t('messageWindow.typeYourResponse')}
                                        className="composer-textarea"
                                        rows={1}
                                    />
                                    <div className="textarea-inline-actions">
                                        <button
                                            type="button"
                                            className="textarea-action-button"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploadingFiles}
                                            title={t('messageWindow.attachFiles')}
                                            aria-label={t('messageWindow.attachFiles')}
                                        >
                                            {uploadingFiles ? (
                                                <FaCircleNotch className="spinner" />
                                            ) : (
                                                <FaFile />
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className="textarea-action-button"
                                            onClick={handleGetAssistantResponse}
                                            disabled={gettingAiHelp}
                                            title={t('messageWindow.aiHelp')}
                                            aria-label={t('messageWindow.aiHelp')}
                                        >
                                            {gettingAiHelp ? (
                                                <FaCircleNotch className="spinner" />
                                            ) : (
                                                <FaLightbulb />
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className="textarea-action-button send-action"
                                            onClick={handleSendMessage}
                                            disabled={sendingMessage || (!newMessage.trim() && pendingFiles.length === 0)}
                                            title={t('messageWindow.send')}
                                        >
                                            {sendingMessage ? (
                                                <>
                                                    <FaCircleNotch className="spinner" /> {t('messageWindow.sending')}
                                                </>
                                            ) : (
                                                <>
                                                    <FaPaperPlane />
                                                    <span>{t('messageWindow.send')}</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileSelection}
                                    multiple
                                    accept="*/*"
                                    style={{ display: 'none' }}
                                />
                            </div>
                        </div>
                    )}
                    <input
                        type="file"
                        ref={draftFileInputRef}
                        onChange={handleDraftFileSelection}
                        multiple
                        accept="*/*"
                        style={{ display: 'none' }}
                    />
                </div>
            </div>

            <div className="side-panel">
                <div className="tab-buttons">
                    <button 
                        className={`tab-button ${activeTab === 'carriers' ? 'active' : ''}`}
                        data-tour="tab-carriers"
                        onClick={() => setActiveTab('carriers')}
                        title={t('messageWindow.carriersConversation')}
                    >
                        <FaComments />
                        <span className="tab-label">{t('messageWindow.tabCarrier', 'Transporteur')}</span>
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'manual' ? 'active' : ''}`}
                        data-tour="tab-manual"
                        onClick={() => setActiveTab('manual')}
                        title={t('messageWindow.manualConversation')}
                    >
                        <FaHandPaper />
                        <span className="tab-label">{t('messageWindow.tabManual', 'Manuel')}</span>
                    </button>
                    {/* Add warehouse tab for edn_api_account users */}
                    {user && user.edn_api_account && (
                        <button 
                            className={`tab-button ${activeTab === 'warehouse' ? 'active' : ''}`}
                            onClick={() => setActiveTab('warehouse')}
                            title={t('messageWindow.warehouseConversation')}
                        >
                            <FaWarehouse />
                            <span className="tab-label">{t('messageWindow.tabWarehouse', 'Entrepôt')}</span>
                        </button>
                    )}
                    {canDisplayColissimoLabels && (
                        <button 
                            className={`tab-button ${activeTab === 'colissimo' ? 'active' : ''}`}
                            onClick={() => setActiveTab('colissimo')}
                            title={t('messageWindow.colissimoLabels')}
                        >
                            <FaShippingFast />
                            <span className="tab-label">{t('messageWindow.tabColissimo', 'Colissimo')}</span>
                        </button>
                    )}
                    <button 
                        className={`tab-button ${activeTab === 'refunds' ? 'active' : ''}`}
                        onClick={() => setActiveTab('refunds')}
                        title={t('refunds.ticketTabTitle', 'Ticket refunds')}
                    >
                        <FaMoneyBillWave />
                        <span className="tab-label">{t('messageWindow.tabRefunds', 'Rembours.')}</span>
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'user' ? 'active' : ''}`}
                        onClick={() => setActiveTab('user')}
                        title={t('messageWindow.userInfo')}
                    >
                        <FaUser />
                        <span className="tab-label">{t('messageWindow.tabUser', 'Client')}</span>
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'track' ? 'active' : ''}`}
                        data-tour="tab-track"
                        onClick={() => setActiveTab('track')}
                        title={t('messageWindow.trackingInfo')}
                    >
                        <FaShippingFast />
                        <span className="tab-label">{t('messageWindow.tabTracking', 'Suivi')}</span>
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'runs' ? 'active' : ''}`}
                        data-tour="tab-runs"
                        onClick={() => setActiveTab('runs')}
                        title={t('messageWindow.aiRunsTitle', 'Trace des traitements IA de ce ticket')}
                    >
                        <FaProjectDiagram />
                        <span className="tab-label">{t('messageWindow.tabAiRuns', 'Runs IA')}</span>
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'actors' ? 'active' : ''}`}
                        data-tour="tab-actors"
                        onClick={() => setActiveTab('actors')}
                        title={t('messageWindow.actorsTitle', 'Everyone involved in this ticket')}
                    >
                        <FaUsers />
                        <span className="tab-label">{t('messageWindow.tabActors', 'Actors')}</span>
                    </button>
                </div>
                
                <div className="tab-content">
                    {activeTab === 'carriers' && (
                        <CarriersConversation 
                            conversation={carriersConversation} 
                            onValidateMessage={handleValidateCarrierMessage}
                            isValidating={validatingMessage}
                            onRefresh={fetchCarriersConversation}
                            ticketFiles={ticketFiles}
                            ticket={ticket}
                        />
                    )}
                    {activeTab === 'manual' && (
                        <ManualConversation 
                            onRefresh={fetchCarriersConversation}
                            ticketFiles={ticketFiles}
                            ticket={ticket}
                        />
                    )}
                    {/* Add warehouse tab content for edn_api_account users */}
                    {activeTab === 'warehouse' && user && user.edn_api_account && (
                        <WarehouseConversation 
                            ticketId={ticketId}
                            onRefresh={fetchWarehouseConversation}
                        />
                    )}
                    {activeTab === 'colissimo' && canDisplayColissimoLabels && (
                        <div className="colissimo-tab">
                            <ColissimoLabels prefilledTicketId={ticketId} />
                        </div>
                    )}
                    {activeTab === 'refunds' && (
                        <TicketRefunds ticketId={ticketId} ticket={ticket} />
                    )}
                    {activeTab === 'actors' && (
                        <TicketActors
                            ticketId={ticketId}
                            ticket={ticket}
                            messages={messages}
                            employeeName={employee}
                            aiAgentName={aiAgentName}
                            carrierConversation={carriersConversation}
                        />
                    )}
                    {activeTab === 'user' && contextData && (
                        <div className="user-tab">
                            <div className="user-section">
                                <div className="user-section-header">
                                    <div className="section-icon">
                                        <FaUser />
                                    </div>
                                    <div>
                                <h3>{t('messageWindow.recipientInfo')}</h3>
                                    </div>
                                </div>

                                <div className="info-grid two-column">
                                    {recipientDetails.company && (
                                        <div className="info-card">
                                            <span className="info-label">
                                                <FaBuilding /> {t('messageWindow.company')}
                                            </span>
                                            <span className="info-value">{recipientDetails.company}</span>
                                        </div>
                                    )}
                                    {recipientDetails.name && (
                                        <div className="info-card">
                                            <span className="info-label">
                                                <FaUser /> {t('messageWindow.name')}
                                            </span>
                                            <span className="info-value">{recipientDetails.name}</span>
                                        </div>
                                    )}
                                </div>

                                {recipientDetails.address && (
                                    <div className="info-subsection">
                                <h4>{t('messageWindow.address')}</h4>
                                        <div className="info-grid two-column">
                                            {recipientDetails.address.street1 && (
                                                <div className="info-card">
                                                    <span className="info-label">
                                                        <FaMapMarkerAlt /> {t('messageWindow.street')}
                                                    </span>
                                                    <span className="info-value">{recipientDetails.address.street1}</span>
                                                </div>
                                            )}
                                            {recipientDetails.address.street2 && (
                                                <div className="info-card">
                                                    <span className="info-label">
                                                        <FaMapMarkerAlt /> {t('messageWindow.street2')}
                                                    </span>
                                                    <span className="info-value">{recipientDetails.address.street2}</span>
                                                </div>
                                            )}
                                            {(recipientDetails.address.postcode || recipientDetails.address.city) && (
                                                <div className="info-card">
                                                    <span className="info-label">
                                                        <FaCity /> {t('messageWindow.city')}
                                                    </span>
                                                    <span className="info-value">
                                                        {[recipientDetails.address.postcode, recipientDetails.address.city]
                                                            .filter(Boolean)
                                                            .join(' ')}
                                                    </span>
                                                </div>
                                            )}
                                            {recipientDetails.address.country && (
                                                <div className="info-card">
                                                    <span className="info-label">
                                                        <FaGlobe /> {t('messageWindow.country')}
                                                    </span>
                                                    <span className="info-value">{recipientDetails.address.country}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {(recipientDetails.contact || clientDetails.email_client || ticket?.customer_email) && (
                                    <div className="info-subsection">
                                <h4>{t('messageWindow.contact')}</h4>
                                        <div className="info-grid two-column">
                                            {recipientDetails.contact?.phone && (
                                                <div className="info-card">
                                                    <span className="info-label">
                                                        <FaPhone /> {t('messageWindow.phone')}
                                                    </span>
                                                    <span className="info-value">{recipientDetails.contact.phone}</span>
                                                </div>
                                            )}
                                            {(clientDetails.email_client || ticket?.customer_email) && (
                                                <div className="info-card">
                                                    <span className="info-label">
                                                        <FaEnvelope /> {t('messageWindow.email')}
                                                    </span>
                                                    <span className="info-value">
                                                        <a className="info-link" href={`mailto:${clientDetails.email_client || ticket?.customer_email}`}>
                                                            {clientDetails.email_client || ticket?.customer_email}
                                                        </a>
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {shippingDetails?.label_url && (
                                    <div className="info-subsection">
                                        <h4>{t('messageWindow.labelUrl')}</h4>
                                        <div className="info-grid">
                                            <div className="info-card">
                                                <span className="info-label">
                                                    <FaFile /> {t('messageWindow.viewLabel')}
                                                </span>
                                                <span className="info-value">
                                                    <a
                                                        className="info-link"
                                                        href={shippingDetails.label_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                            {t('messageWindow.viewLabel')}
                                        </a>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="user-section">
                                <div className="user-section-header">
                                    <div className="section-icon">
                                        <FaIdCard />
                                    </div>
                                    <div>
                                <h3>{t('messageWindow.clientInfo')}</h3>
                                    </div>
                                </div>

                                <div className="info-grid two-column">
                                    {recipientDetails.contact?.email && (
                                        <div className="info-card">
                                            <span className="info-label">
                                                <FaGlobe /> {t('messageWindow.client_email')}
                                            </span>
                                            <span className="info-value">
                                                <a className="info-link" href={`mailto:${recipientDetails.contact.email}`}>
                                                    {recipientDetails.contact.email}
                                                </a>
                                            </span>
                                        </div>
                                    )}
                                    {clientDetails.id && (
                                        <div className="info-card">
                                            <span className="info-label">
                                                <FaIdCard /> {t('messageWindow.clientId')}
                                            </span>
                                            <span className="info-value">{clientDetails.id}</span>
                                        </div>
                                    )}
                                    {clientDetails.name && (
                                        <div className="info-card">
                                            <span className="info-label">
                                                <FaUser /> {t('messageWindow.clientName')}
                                            </span>
                                            <span className="info-value">{clientDetails.name}</span>
                                        </div>
                                    )}
                                    {clientDetails.status && (
                                        <div className="info-card">
                                            <span className="info-label">
                                                <FaInfo /> {t('messageWindow.clientStatus')}
                                            </span>
                                            <span className="info-value">{clientDetails.status}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="user-section">
                                <div className="user-section-header">
                                    <div className="section-icon">
                                        <FaTicketAlt />
                                    </div>
                                    <div>
                                        <h3>{t('messageWindow.clientTickets')}</h3>
                                    </div>
                                </div>

                                {clientTicketsLoading && (
                                    <div className="info-placeholder">
                                        <FaCircleNotch className="spinner" /> {t('messageWindow.loading')}
                                    </div>
                                )}

                                {!clientTicketsLoading && clientTicketsError && (
                                    <div className="info-placeholder error">
                                        <FaExclamationTriangle /> {clientTicketsError}
                                    </div>
                                )}

                                {!clientTicketsLoading && !clientTicketsError && clientTickets.length === 0 && (
                                    <div className="info-placeholder">{t('messageWindow.noClientTickets')}</div>
                                )}

                                {!clientTicketsLoading && !clientTicketsError && clientTickets.length > 0 && (
                                    <div className="client-tickets-list">
                                        {(showAllClientTickets ? clientTickets : clientTickets.slice(0, 3)).map((clientTicket) => {
                                            const isActiveTicket = clientTicket.id === ticketId;
                                            const statusClassName = clientTicket.status
                                                ? `status-badge ${clientTicket.status}`
                                                : null;

                                            return (
                                                <button
                                                    key={clientTicket.id}
                                                    type="button"
                                                    className={`info-card client-ticket-card ${isActiveTicket ? 'active' : ''}`}
                                                    onClick={() => {
                                                        if (!isActiveTicket) {
                                                            navigate(`/tickets/${clientTicket.id}/messages`);
                                                        }
                                                    }}
                                                    disabled={isActiveTicket}
                                                >
                                                    <div className="client-ticket-header">
                                                        <span className="client-ticket-subject">
                                                            {clientTicket.subject || t('messageWindow.noSubject')}
                                                        </span>
                                                        {statusClassName && (
                                                            <span className={statusClassName}>
                                                                {getStatusLabel(t, clientTicket.status)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="client-ticket-meta">
                                                        {clientTicket.updated_at && (
                                                            <span className="client-ticket-timestamp">
                                                                <FaClock /> {formatDateTime(clientTicket.updated_at)}
                                                            </span>
                                                        )}
                                                        <span className="client-ticket-link">
                                                            {isActiveTicket
                                                                ? t('messageWindow.currentTicket')
                                                                : t('messageWindow.openTicket')}
                                                            {!isActiveTicket && <FaArrowRight />}
                                                        </span>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                        {clientTickets.length > 3 && (
                                            <button
                                                type="button"
                                                className="expand-tickets-button"
                                                onClick={() => setShowAllClientTickets(!showAllClientTickets)}
                                            >
                                                {showAllClientTickets ? (
                                                    <>
                                                        <FaChevronUp /> {t('messageWindow.showLess', 'Voir moins')}
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaChevronDown /> {t('messageWindow.showMore', `Voir ${clientTickets.length - 3} de plus`)}
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="user-section">
                                <div className="user-section-header">
                                    <div className="section-icon">
                                    <FaBox />
                                    </div>
                                    <div>
                                        <h3>{t('messageWindow.packageInfo')}</h3>
                                    </div>
                                </div>

                                <div className="info-grid two-column">
                                    <PackageIdentifierCards orderInfo={orderDetailsData} />
                                    {orderDetailsData.goods_value != null && (
                                        <div className="info-card">
                                            <span className="info-label">
                                                <FaMoneyBillWave /> {t('messageWindow.goodsValue')}
                                            </span>
                                            <span className="info-value">
                                                {`${orderDetailsData.goods_value} ${orderDetailsData.currency || ''}`.trim()}
                                            </span>
                                        </div>
                                    )}
                                    {orderDetailsData.package_value != null && (
                                        <div className="info-card">
                                            <span className="info-label">
                                                <FaBoxOpen /> {t('messageWindow.packageValue')}
                                            </span>
                                            <span className="info-value">
                                                {`${orderDetailsData.package_value} ${orderDetailsData.currency || ''}`.trim()}
                                            </span>
                                        </div>
                                    )}
                                    {orderDetailsData.insurance_value != null && (
                                        <div className="info-card">
                                            <span className="info-label">
                                                <FaShieldAlt /> {t('messageWindow.insuranceValue')}
                                            </span>
                                            <span className="info-value">
                                                {`${orderDetailsData.insurance_value} ${orderDetailsData.currency || ''}`.trim()}
                                            </span>
                                        </div>
                                    )}
                                    {orderDetailsData.ht_price !== null && orderDetailsData.ht_price !== undefined && (
                                        <div className="info-card">
                                            <span className="info-label">
                                                <FaReceipt /> {t('messageWindow.htPrice')}
                                            </span>
                                            <span className="info-value">
                                                {`${orderDetailsData.ht_price} ${orderDetailsData.currency || ''}`.trim()}
                                            </span>
                                        </div>
                                )}
                            </div>

                                {orderDetailsData.package_content && (
                                    <div className="info-card info-card-wide">
                                        <span className="info-label">
                                            <FaInfo /> {t('messageWindow.packageContent')}
                                        </span>
                                        <span className="info-value">{orderDetailsData.package_content}</span>
                                    </div>
                                )}
                            </div>

                            {compatibilityDetails && (
                                <div className="user-section">
                                    <div className="user-section-header warning">
                                        <div className="section-icon warning">
                                            <FaExclamationTriangle />
                                        </div>
                                        <div>
                                            <h3>{t('messageWindow.compatibilityResult', 'Compatibility Result')}</h3>
                                        </div>
                                    </div>

                                    {compatibilitySerialNumber && (
                                        <div className="info-card compatibility-serial-card">
                                            <span className="info-label">
                                                <FaBarcode /> {t('messageWindow.serialNumber', 'Serial Number')}
                                            </span>
                                            <span className="info-value">{compatibilitySerialNumber}</span>
                                        </div>
                                    )}

                                    {compatibilityPartList.length > 0 && (
                                        <div className="info-subsection">
                                            <h4>{t('messageWindow.compatibility.parts', 'Parts')}</h4>
                                            <div className="compatibility-parts-grid">
                                                {compatibilityPartList.map((part, index) => {
                                                    const displayName = getCleanPartName(part) || part.part_number || t('messageWindow.compatibility.partNumber', 'Part Number');
                                                    const partSku = getPartSku(part);
                                                    const partDesc = part?.part_desc || part?.partDescription || part?.description;
                                                    return (
                                                        <div
                                                            key={part.part_number || part.part_name || index}
                                                            className="compatibility-card compatibility-part-card"
                                                        >
                                                            <div className="compatibility-part-header">
                                                                <FaBarcode />
                                                                <div className="compatibility-part-header-text">
                                                                    <span className="compatibility-part-name">
                                                                        {displayName}
                                                                    </span>
                                                                    {partDesc && (
                                                                        <span className="compatibility-part-desc">
                                                                            {partDesc}
                                                                        </span>
                                                                    )}
                                                                    <div className="compatibility-part-badges">
                                                                        {part.part_number && (
                                                                            <span
                                                                                className="compatibility-chip pn"
                                                                                title={t('messageWindow.compatibility.partNumber', 'Part Number')}
                                                                            >
                                                                                {part.part_number}
                                                                            </span>
                                                                        )}
                                                                        {partSku && (
                                                                            <span
                                                                                className="compatibility-chip sku"
                                                                                title={t('messageWindow.compatibility.sku', 'SKU')}
                                                                            >
                                                                                {partSku}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="compatibility-part-details">
                                                                {part.part_group && (
                                                                    <div className="compatibility-detail">
                                                                        <span className="compatibility-detail-label">
                                                                            {t('messageWindow.compatibility.partGroup', 'Part Group')}
                                                                        </span>
                                                                        <span className="compatibility-detail-value">{part.part_group}</span>
                                                                    </div>
                                                                )}
                                                                {displayName && (
                                                                    <div className="compatibility-detail">
                                                                        <span className="compatibility-detail-label">
                                                                            {t('messageWindow.compatibility.partName', 'Part Name')}
                                                                        </span>
                                                                        <span className="compatibility-detail-value">{displayName}</span>
                                                                    </div>
                                                                )}
                                                                {partDesc && (
                                                                    <div className="compatibility-detail">
                                                                        <span className="compatibility-detail-label">
                                                                            {t('messageWindow.compatibility.partDesc', 'Description')}
                                                                        </span>
                                                                        <span className="compatibility-detail-value">{partDesc}</span>
                                                                    </div>
                                                                )}
                                                                {partSku && (
                                                                    <div className="compatibility-detail">
                                                                        <span className="compatibility-detail-label">
                                                                            {t('messageWindow.compatibility.sku', 'SKU')}
                                                                        </span>
                                                                        <span className="compatibility-detail-value">{partSku}</span>
                                                                    </div>
                                                                )}
                                                                {part.url && (
                                                                    <a 
                                                                        className="info-link"
                                                                        href={part.url} 
                                                                        target="_blank" 
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        {t('messageWindow.compatibility.viewPart', 'View Part')}
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                        </div>
                                                    </div>
                                    )}

                                    {compatibilityMetaEntries.length > 0 && (
                                        <div className="compatibility-meta-grid">
                                            {compatibilityMetaEntries.map(([key, value]) => {
                                                const formattedValue = formatCompatibilityValue(value);
                                                const label = t(`messageWindow.compatibility.${key}`, key.replace(/_/g, ' '));
                                                const isComplexValue = value && typeof value === 'object';
                                            return (
                                                    <div className="compatibility-card" key={key}>
                                                        <span className="info-label">{label}</span>
                                                        {isComplexValue ? (
                                                            <pre className="compatibility-json">{formattedValue}</pre>
                                                        ) : (
                                                            <span className="info-value">{formattedValue}</span>
                                                        )}
                                                    </div>
                                            );
                                        })}
                                    </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {activeTab === 'runs' && (
                        <>
                            {/* An approved carrier-contact proposal runs the
                                contact before answering: reload the carrier
                                panel like after a draft send (KI-184). */}
                            <AiProposalsPanel ticketId={ticketId} onChanged={fetchCarriersConversation} />
                            <AiRunPanel
                                ticketId={ticketId}
                                ticket={ticket}
                                onOpenRun={(runId) => openAiRun(runId, null)}
                                onOpenTimeline={() => openAiRun(null, null)}
                                onRerunDone={() => {
                                    // The enrichment is written synchronously; the AI restart
                                    // finishes in the background a few seconds later.
                                    fetchTicketContext();
                                    setTimeout(() => { fetchMessages(); fetchTicketContext(); }, 6000);
                                }}
                            />
                        </>
                    )}
                    {activeTab === 'track' && contextData && (
                        <div className="track-tab">
                            <div className="tracking-section">
                                <h3>{t('messageWindow.shippingInfo')}</h3>
                                
                                {contextData.shipping_info.tracking_url && (
                                    <p className="tracking-link">
                                        <a href={contextData.shipping_info.tracking_url} target="_blank" rel="noopener noreferrer">
                                            {t('messageWindow.trackingLink', "Track on the carrier's website")}
                                        </a>
                                    </p>
                                )}
                                {contextData.shipping_info.tracking_info && (
                                    <div className="tracking-info">
                                        {contextData.shipping_info.tracking_info.status && (
                                            <p><strong>{t('messageWindow.currentStatus')}:</strong> {contextData.shipping_info.tracking_info.status}</p>
                                        )}
                                        {contextData.shipping_info.tracking_info.fetched_at && (
                                            <p className="tracking-refreshed">
                                                {t('messageWindow.trackingRefreshedAt', 'Tracking refreshed on')} {formatDateTime(contextData.shipping_info.tracking_info.fetched_at)}
                                                {contextData.shipping_info.tracking_info.source ? ` · ${contextData.shipping_info.tracking_info.source}` : ''}
                                            </p>
                                        )}
                                        {!(contextData.shipping_info.tracking_info.events &&
                                           contextData.shipping_info.tracking_info.events.length > 0) && (
                                            <p className="tracking-empty">{t('messageWindow.noTrackingEventsYet', 'No tracking events received yet')}</p>
                                        )}
                                        
                                        {contextData.shipping_info.tracking_info.events && 
                                         contextData.shipping_info.tracking_info.events.length > 0 && (
                                            <>
                                                <h4>{t('messageWindow.trackingHistory')}</h4>
                                                <div className="tracking-events">
                                                    {contextData.shipping_info.tracking_info.events.map((event, index) => (
                                                        <div key={index} className="tracking-event">
                                                            <div className="event-date">
                                                                {formatTrackingEventDate(event)}
                                                            </div>
                                                            {event.location && (
                                                                <div className="event-location">
                                                                    {event.location}
                                                                </div>
                                                            )}
                                                            <div className="event-status">
                                                                {event.status}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Note Modal */}
            {showNoteModal && (
                <div className="note-modal-overlay">
                    <div className="note-modal">
                        <div className="note-modal-header">
                            <h3>{t('messageWindow.addNote')}</h3>
                            <button className="note-modal-close" onClick={closeNoteModal}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="note-modal-content">
                            <div className="note-message-preview">
                                <p><strong>{t('messageWindow.messagePreview')}:</strong></p>
                                <div className="preview-content">
                                    {selectedMessageForNote?.content.substring(0, 100)}
                                    {selectedMessageForNote?.content.length > 100 && '...'}
                                </div>
                            </div>
                            <textarea
                                value={noteContent}
                                onChange={(e) => setNoteContent(e.target.value)}
                                placeholder={t('messageWindow.noteContentPlaceholder')}
                                className="note-textarea"
                                rows={4}
                                maxLength={500}
                            />
                            <div className="note-char-count">
                                {noteContent.length}/500
                            </div>
                        </div>
                        <div className="note-modal-actions">
                            <button 
                                className="note-cancel-button" 
                                onClick={closeNoteModal}
                                disabled={addingNote}
                            >
                                {t('messageWindow.cancel')}
                            </button>
                            <button 
                                className="note-save-button" 
                                onClick={handleAddNote}
                                disabled={!noteContent.trim() || addingNote}
                            >
                                {addingNote ? (
                                    <>
                                        <FaCircleNotch className="spinner" /> {t('messageWindow.adding')}
                                    </>
                                ) : (
                                    <>
                                        <FaPlus /> {t('messageWindow.addNote')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* File Overlay */}
            {selectedFile && (
                <div className="file-overlay">
                    <button
                        type="button"
                        className="file-nav-button prev"
                        onClick={() => handleAttachmentNavigation(-1)}
                        disabled={selectedAttachmentIndex === null || selectedAttachmentIndex === 0}
                        title={t('messageWindow.previousAttachment', 'Previous attachment')}
                        aria-label={t('messageWindow.previousAttachment', 'Previous attachment')}
                    >
                        <FaArrowLeft />
                    </button>
                    <div className={`file-overlay-content ${selectedFile.mime_type?.includes('image') ? '' : 'is-doc'}`}>
                        <div className="file-overlay-counter">
                            {selectedAttachmentIndex !== null && totalAttachments > 0
                                ? `${selectedAttachmentIndex + 1} / ${totalAttachments}`
                                : ''}
                        </div>
                        <button className="close-overlay" onClick={closeFileOverlay} title={t('messageWindow.close', 'Close attachment')} aria-label={t('messageWindow.close', 'Close attachment')}>
                            <FaTimesCircle size={30} />
                        </button>
                        {selectedFile.reading && (
                            <div className="file-overlay-reading">
                                <div className="file-overlay-reading-title">{selectedFile.reading.title}</div>
                                {selectedFile.reading.description && (
                                    <p className="file-overlay-reading-description">{selectedFile.reading.description}</p>
                                )}
                                <div className="file-overlay-reading-meta">
                                    <FaRobot aria-hidden="true" />
                                    <span>
                                        {readingDate(selectedFile.reading)
                                            ? t('messageWindow.readByAiOn', "Lu par l'IA le {{date}}", { date: readingDate(selectedFile.reading) })
                                            : t('messageWindow.readByAi', "Lu par l'IA")}
                                        {selectedFile.reading.document_type_label ? ` · ${selectedFile.reading.document_type_label}` : ''}
                                        {` · ${selectedFile.name}`}
                                    </span>
                                </div>
                            </div>
                        )}
                        <div className="file-overlay-body">
                            {selectedFile.mime_type === 'application/pdf' ? (
                                <object
                                    data={selectedFile.url}
                                    type="application/pdf"
                                >
                                    <p>
                                        {t('messageWindow.unableToDisplayFile')}. <a href={selectedFile.url}> {t('messageWindow.downloadFile')}</a>
                                    </p>
                                </object>
                            ) : selectedFile.mime_type?.includes('image') ? (
                                <img src={selectedFile.url} alt={selectedFile.name} />
                            ) : (
                                <iframe 
                                    src={selectedFile.url} 
                                    title={selectedFile.name} 
                                ></iframe>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="file-nav-button next"
                        onClick={() => handleAttachmentNavigation(1)}
                        disabled={
                            selectedAttachmentIndex === null ||
                            selectedAttachmentIndex >= totalAttachments - 1
                        }
                        title={t('messageWindow.nextAttachment', 'Next attachment')}
                        aria-label={t('messageWindow.nextAttachment', 'Next attachment')}
                    >
                        <FaArrowRight />
                    </button>
                </div>
            )}

            {/* Overlay for success/error messages */}
            {overlayState.show && (
                <div className="overlay">
                    <div className={`overlay-content ${overlayState.success ? 'success' : 'error'}`}>
                        <p>{overlayState.message}</p>
                    </div>
                </div>
            )}

            {ednVisibilityModal.show && (
                <div className="edn-visibility-modal-overlay">
                    <div className="edn-visibility-modal">
                        <div className="edn-visibility-modal-header">
                            <h3>{t('messageWindow.chooseVisibility', 'Choose message visibility')}</h3>
                            <button className="edn-visibility-modal-close" onClick={closeEdnVisibilityModal}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="edn-visibility-modal-content">
                            <p>
                                {t(
                                    'messageWindow.chooseVisibilityDescription',
                                    'Choose who can see this message in EDN before sending.'
                                )}
                            </p>
                            <div className="edn-visibility-options">
                                <button
                                    type="button"
                                    className="edn-visibility-option"
                                    onClick={() => handleEdnVisibilitySelect('admin')}
                                >
                                    <FaUser />
                                    <span>{t('messageWindow.visibilityAdmin', 'Admin only')}</span>
                                </button>
                                <button
                                    type="button"
                                    className="edn-visibility-option primary"
                                    onClick={() => handleEdnVisibilitySelect('all')}
                                >
                                    <FaGlobe />
                                    <span>{t('messageWindow.visibilityAll', 'All')}</span>
                                </button>
                            </div>
                        </div>
                        <div className="edn-visibility-modal-actions">
                            <button className="edn-visibility-cancel-button" onClick={closeEdnVisibilityModal}>
                                {t('messageWindow.cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showStatusHistoryModal && (
                <div className="status-history-modal-overlay">
                    <div className="status-history-modal">
                        <div className="status-history-modal-header">
                            <h3>{t('messageWindow.statusHistory', 'Status history')}</h3>
                            <button
                                className="status-history-modal-close"
                                onClick={() => setShowStatusHistoryModal(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="status-history-modal-content">
                            {loadingStatusHistory ? (
                                <div className="status-history-loading">
                                    <FaCircleNotch className="spinner" />
                                    <span>{t('messageWindow.loadingStatusHistory', 'Loading status history...')}</span>
                                </div>
                            ) : (
                                <>
                                    <div className="status-history-current">
                                        <span>{t('messageWindow.currentStatus', 'Current status')}</span>
                                        <strong>{getEdnStatusLabel(statusHistoryData?.current_status || ticket.status)}</strong>
                                    </div>
                                    {statusHistoryData?.status_history?.length > 0 ? (
                                        <div className="status-history-list">
                                            {statusHistoryData.status_history.map((entry) => (
                                                <div key={entry.id} className="status-history-entry">
                                                    <div className="status-history-entry-marker" />
                                                    <div className="status-history-entry-body">
                                                        <div className="status-history-change">
                                                            <span>{getEdnStatusLabel(entry.previous_status)}</span>
                                                            <FaArrowRight />
                                                            <strong>{getEdnStatusLabel(entry.new_status)}</strong>
                                                        </div>
                                                        <div className="status-history-meta">
                                                            <span>{formatDate(entry.created_at)}</span>
                                                            {entry.changed_by?.name && (
                                                                <span>{entry.changed_by.name}</span>
                                                            )}
                                                            {!entry.changed_by?.name && entry.changed_by?.email && (
                                                                <span>{entry.changed_by.email}</span>
                                                            )}
                                                            {entry.source && (
                                                                <span>{entry.source}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="status-history-empty">
                                            {t('messageWindow.noStatusHistory', 'No status history available for this ticket.')}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="status-history-modal-actions">
                            <button
                                className="status-history-close-button"
                                onClick={() => setShowStatusHistoryModal(false)}
                            >
                                {t('messageWindow.close', 'Close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* AI Handoff Modal */}
            {showHandoffModal && (
                <div className="handoff-modal-overlay">
                    <div className="handoff-modal">
                        <div className="handoff-modal-header">
                            <h3>{t('messageWindow.handoffToAI')}</h3>
                            <button className="handoff-modal-close" onClick={closeHandoffModal}>
                                <FaTimes />
                            </button>
                        </div>
            <div className="handoff-modal-content">
                <div className="handoff-info">
                    <p className="handoff-description">
                        {t(
                            'messageWindow.handoffDescription',
                            'Provide instructions for the AI agent to handle this ticket. The AI will generate a response based on your instructions.'
                        )}
                    </p>
                    <div className="handoff-current-assignment">
                        <p>
                            <strong>{t('messageWindow.currentlyAssignedTo')}:</strong> {ticket.employee_assigned}
                        </p>
                    </div>
                </div>
                <textarea
                    value={handoffInstructions}
                    onChange={(e) => setHandoffInstructions(e.target.value)}
                    placeholder={t(
                        'messageWindow.handoffInstructionsPlaceholder',
                        'Example: Provide a refund for this order, or Please follow up with the carrier about the delivery status...'
                    )}
                    className="handoff-textarea"
                    rows={6}
                    maxLength={2000}
                />
                <div className="handoff-char-count">
                    {handoffInstructions.length}/2000
                </div>
            </div>
                        <div className="handoff-modal-actions">
                            <button 
                                className="handoff-cancel-button" 
                                onClick={closeHandoffModal}
                                disabled={handingOffToAI}
                            >
                                {t('messageWindow.cancel')}
                            </button>
                            <button 
                                className="handoff-submit-button" 
                                onClick={handleAIHandoff}
                                disabled={!handoffInstructions.trim() || handingOffToAI}
                            >
                                {handingOffToAI ? (
                                    <>
                                        <FaCircleNotch className="spinner" /> {t('messageWindow.handingOff')}
                                    </>
                                ) : (
                                    <>
                                        <FaRobot /> {t('messageWindow.handoffToAI')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Slack Message Modal */}
            {showSlackModal && (
                <div className="slack-modal-overlay">
                    <div className="slack-modal">
                        <div className="slack-modal-header">
                            <h3>{t('messageWindow.sendSlackMessage')}</h3>
                            <button className="slack-modal-close" onClick={closeSlackModal}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="slack-modal-content">
                            <div className="slack-info">
                                <p className="slack-description">
                                    {t('messageWindow.slackDescription', 
                                        'Send a message to the support team via Slack. Include any relevant information about this ticket that requires attention.')}
                                </p>
                                <div className="slack-ticket-info">
                                    <p>
                                        <strong>{t('messageWindow.ticketId')}:</strong> {ticketId}
                                    </p>
                                    <p>
                                        <strong>{t('messageWindow.subject')}:</strong> {ticket.subject}
                                    </p>
                                </div>
                            </div>
                            <textarea
                                value={slackMessage}
                                onChange={(e) => setSlackMessage(e.target.value)}
                                placeholder={t('messageWindow.slackMessagePlaceholder', 
                                    'Describe the issue or question you need help with from the support team...')}
                                className="slack-textarea"
                                rows={6}
                                maxLength={2000}
                            />
                            <div className="slack-char-count">
                                {slackMessage.length}/2000
                            </div>
                        </div>
                        <div className="slack-modal-actions">
                            <button 
                                className="slack-cancel-button" 
                                onClick={closeSlackModal}
                                disabled={sendingSlackMessage}
                            >
                                {t('messageWindow.cancel')}
                            </button>
                            <button 
                                className="slack-submit-button" 
                                onClick={handleSendSlackMessage}
                                disabled={!slackMessage.trim() || sendingSlackMessage}
                            >
                                {sendingSlackMessage ? (
                                    <>
                                        <FaCircleNotch className="spinner" /> {t('messageWindow.sending')}
                                    </>
                                ) : (
                                    <>
                                        <FaSlack /> {t('messageWindow.sendToSlack')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showBugReportModal && (
                <div className="bug-report-modal-overlay">
                    <div className="bug-report-modal">
                        <div className="bug-report-modal-header">
                            <h3>{t('messageWindow.reportBug', 'Report a bug')}</h3>
                            <button className="bug-report-modal-close" onClick={closeBugReportModal}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="bug-report-modal-content">
                            <div className="bug-report-info">
                                <p className="bug-report-description">
                                    {t('messageWindow.bugReportDescription',
                                        'Describe the problem you faced on this ticket. The support team will receive an email with this ticket details and your description.')}
                                </p>
                                <div className="bug-report-ticket-info">
                                    <p>
                                        <strong>{t('messageWindow.ticketId', 'Ticket ID')}:</strong> {ticketId}
                                    </p>
                                    <p>
                                        <strong>{t('messageWindow.subject', 'Subject')}:</strong> {ticket.subject}
                                    </p>
                                </div>
                            </div>
                            <textarea
                                value={bugDescription}
                                onChange={(e) => setBugDescription(e.target.value)}
                                placeholder={t('messageWindow.bugReportPlaceholder',
                                    'Describe the problem you encountered on this ticket...')}
                                className="bug-report-textarea"
                                rows={6}
                                maxLength={2000}
                            />
                            <div className="bug-report-char-count">
                                {bugDescription.length}/2000
                            </div>
                        </div>
                        <div className="bug-report-modal-actions">
                            <button 
                                className="bug-report-cancel-button" 
                                onClick={closeBugReportModal}
                                disabled={sendingBugReport}
                            >
                                {t('messageWindow.cancel')}
                            </button>
                            <button 
                                className="bug-report-submit-button" 
                                onClick={handleSendBugReport}
                                disabled={!bugDescription.trim() || sendingBugReport}
                            >
                                {sendingBugReport ? (
                                    <>
                                        <FaCircleNotch className="spinner" /> {t('messageWindow.sending')}
                                    </>
                                ) : (
                                    <>
                                        <FaBug /> {t('messageWindow.sendBugReport', 'Send bug report')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showForwardModal && (
                <div className="forward-modal-overlay">
                    <div className="forward-modal">
                        <div className="forward-modal-header">
                            <h3>{t('messageWindow.forwardEmail', 'Forward by email')}</h3>
                            <button className="forward-modal-close" onClick={closeForwardModal}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="forward-modal-content">
                            <div className="forward-info">
                                <p className="forward-description">
                                    {ticket.outlook_conversation_id
                                        ? t('messageWindow.forwardDescription',
                                            'Forward the latest email of this conversation to one or more recipients. Separate multiple addresses with a comma.')
                                        : t('messageWindow.forwardPdfDescription',
                                            'This conversation will be sent as a PDF attachment from your connected Outlook account to one or more recipients. Separate multiple addresses with a comma.')}
                                </p>
                                <div className="forward-ticket-info">
                                    <p>
                                        <strong>{t('messageWindow.subject', 'Subject')}:</strong> {ticket.subject}
                                    </p>
                                </div>
                            </div>
                            <div className="forward-field">
                                <label htmlFor="forward-to-input">
                                    {t('messageWindow.forwardTo', 'To')} *
                                </label>
                                <input
                                    id="forward-to-input"
                                    type="text"
                                    value={forwardToInput}
                                    onChange={(e) => setForwardToInput(e.target.value)}
                                    placeholder={t('messageWindow.forwardToPlaceholder', 'recipient@example.com, other@example.com')}
                                    className="forward-input"
                                    disabled={forwardingEmail}
                                />
                            </div>
                            <div className="forward-field">
                                <label htmlFor="forward-cc-input">
                                    {t('messageWindow.forwardCc', 'CC')}
                                </label>
                                <input
                                    id="forward-cc-input"
                                    type="text"
                                    value={forwardCcInput}
                                    onChange={(e) => setForwardCcInput(e.target.value)}
                                    placeholder={t('messageWindow.forwardCcPlaceholder', 'cc@example.com (optional)')}
                                    className="forward-input"
                                    disabled={forwardingEmail}
                                />
                            </div>
                            <div className="forward-field">
                                <label htmlFor="forward-comment-input">
                                    {t('messageWindow.forwardComment', 'Comment')}
                                </label>
                                <textarea
                                    id="forward-comment-input"
                                    value={forwardComment}
                                    onChange={(e) => setForwardComment(e.target.value)}
                                    placeholder={t('messageWindow.forwardCommentPlaceholder', 'Add an optional comment to include with the forwarded email...')}
                                    className="forward-textarea"
                                    rows={4}
                                    maxLength={2000}
                                    disabled={forwardingEmail}
                                />
                            </div>
                            {forwardToInput.trim() && !forwardEmailsValid && (
                                <div className="forward-validation-error">
                                    {t('messageWindow.forwardInvalidEmails', 'Please enter valid email addresses')}
                                </div>
                            )}
                        </div>
                        <div className="forward-modal-actions">
                            <button
                                className="forward-cancel-button"
                                onClick={closeForwardModal}
                                disabled={forwardingEmail}
                            >
                                {t('messageWindow.cancel')}
                            </button>
                            <button
                                className="forward-submit-button"
                                onClick={handleForwardEmail}
                                disabled={!forwardEmailsValid || forwardingEmail}
                            >
                                {forwardingEmail ? (
                                    <>
                                        <FaCircleNotch className="spinner" /> {t('messageWindow.sending')}
                                    </>
                                ) : (
                                    <>
                                        <FaShareSquare /> {t('messageWindow.forwardSend', 'Forward')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showSummaryModal && (
                <div className="summary-modal-overlay">
                    <div className="summary-modal">
                        <div className="summary-modal-header">
                            <h3>{t('messageWindow.ticketSummary', 'Ticket Summary')}</h3>
                            <button className="summary-modal-close" onClick={() => setShowSummaryModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="summary-modal-content">
                            {loadingSummary ? (
                                <div className="summary-loading">
                                    <FaCircleNotch className="spinner" />
                                    <p>{t('messageWindow.generatingSummary', 'Generating summary...')}</p>
                                </div>
                            ) : (
                                <div className="summary-text">
                                    {ticketSummary.split('\n').map((line, i) => (
                                        <p key={i}>{line ? <LinkifiedText text={line} /> : '\u00A0'}</p>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="summary-modal-actions">
                            <button
                                className="summary-close-button"
                                onClick={() => setShowSummaryModal(false)}
                            >
                                {t('messageWindow.close', 'Close')}
                            </button>
                            <button
                                className="summary-refresh-button"
                                onClick={() => handleGetTicketSummary(true)}
                                disabled={loadingSummary}
                            >
                                {loadingSummary ? (
                                    <><FaCircleNotch className="spinner" /> {t('messageWindow.generatingSummary', 'Generating...')}</>
                                ) : (
                                    <><FaSync /> {t('messageWindow.regenerate', 'Regenerate')}</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Restart AI Processing Modal */}
            {showRestartAIModal && (
                <div className="restart-ai-modal-overlay">
                    <div className="restart-ai-modal">
                        <div className="restart-ai-modal-header">
                            <h3>{t('messageWindow.restartAIProcessing', 'Restart AI Processing')}</h3>
                            <button className="restart-ai-modal-close" onClick={closeRestartAIModal}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="restart-ai-modal-content">
                            <div className="restart-ai-info">
                                <p className="restart-ai-description">
                                    {t('messageWindow.restartAIDescription', 
                                        'Select a different AI assistant to reprocess this ticket. This will delete existing AI responses and generate a new one using the selected assistant.')}
                                </p>
                                <div className="restart-ai-warning">
                                    <FaExclamationTriangle />
                                    <span>{t('messageWindow.restartAIWarning', 'Warning: This will delete all AI-generated messages for this ticket.')}</span>
                                </div>
                            </div>
                            
                            <div className="restart-ai-assistant-selector">
                                <label>{t('messageWindow.selectAssistant', 'Select AI Assistant')}</label>
                                {assistantsLoading ? (
                                    <div className="restart-ai-loading">
                                        <FaCircleNotch className="spinner" />
                                        {t('messageWindow.loadingAssistants', 'Loading assistants...')}
                                    </div>
                                ) : availableAssistants.length > 0 ? (
                                    <div className="restart-ai-assistant-list">
                                        {availableAssistants.map((assistant) => (
                                            <button
                                                key={assistant._id}
                                                type="button"
                                                className={`restart-ai-assistant-item ${selectedAssistantId === assistant.assistant_id ? 'selected' : ''}`}
                                                onClick={() => setSelectedAssistantId(assistant.assistant_id)}
                                            >
                                                <div className="assistant-item-icon">
                                                    <FaRobot />
                                                </div>
                                                <div className="assistant-item-info">
                                                    <span className="assistant-item-name">{assistant.name}</span>
                                                    {assistant.is_running && (
                                                        <span className="assistant-item-badge running">
                                                            {t('messageWindow.running', 'Running')}
                                                        </span>
                                                    )}
                                                </div>
                                                {selectedAssistantId === assistant.assistant_id && (
                                                    <FaCheck className="assistant-item-check" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="restart-ai-no-assistants">
                                        {t('messageWindow.noAssistantsAvailable', 'No AI assistants available')}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="restart-ai-modal-actions">
                            <button 
                                className="restart-ai-cancel-button" 
                                onClick={closeRestartAIModal}
                                disabled={restartingAI}
                            >
                                {t('messageWindow.cancel')}
                            </button>
                            <button 
                                className="restart-ai-submit-button" 
                                onClick={handleRestartAIProcessing}
                                disabled={!selectedAssistantId || restartingAI}
                            >
                                {restartingAI ? (
                                    <>
                                        <FaCircleNotch className="spinner" /> {t('messageWindow.restarting', 'Restarting...')}
                                    </>
                                ) : (
                                    <>
                                        <FaSync /> {t('messageWindow.restartProcessing', 'Restart Processing')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        {aiTimelineOpen && (
                <AiTimelineOverlay
                    ticketId={ticketId}
                    ticketSubject={ticket?.subject}
                    focusRunId={aiRunFocus?.runId}
                    focusSpanId={aiRunFocus?.spanId}
                    focusKey={aiRunFocus?.at}
                    onClose={closeAiTimeline}
                />
            )}
            </div>
    );
};

export default MessageWindow;

